import _ from 'lodash'
import { CanvasEngineGrabController } from './grabController'
import type {
  CanvasEngineSoftLayer,
  Vector2d,
  DirectionLineType,
  MagnifierState,
  MagnifierPosition,
} from './types'
import {
  CANVAS_STATE,
  CANVAS_HANDLE_MODE,
  SHAPES,
  CANVAS_HANDLE_EVENT,
  CANVAS_CREATE_METHOD,
  DIR_DRAW_DIRECTION_THRESHOLD,
  DIR_DRAW_MAGNIFYING_GLASS_THRESHOLD,
  MAGNIFIER_RADIUS,
  MAGNIFIER_SCALE,
  MAGNIFIER_BORDER_WIDTH,
  MAGNIFIER_BORDER_COLOR,
  MAGNIFIER_VIEWPORT_MARGIN,
  MAGNIFIER_OFFSET,
  RECT_DRAG_GUIDE_COLOR,
  RECT_DRAG_GUIDE_OPACITY,
  RECT_DRAG_GUIDE_DASH,
  RECT_DRAG_MIN_DISTANCE,
} from './const'
import { CanvasEngineEntity } from './entity'
import { getDistance, getCenter } from './helper'
import { eraseFromLine, getEraserRadius } from './helper/brushEraser'
import { isShapeBrushPoints } from './entity/types/brush/brushEntity'
import {
  snapToDirections,
  calculateDirectionPath,
  updateDirectionRender,
  createTempArrow,
  createDirectionHeadArrow,
  updateTempArrow,
} from './entity/types/direction/stateManager'
import { EventManager } from './eventManager'
import type {
  IArrow,
  ICircle,
  IGroup,
  ILine,
  IMagnifierBackend,
  INode,
  IRect,
  PointerEventOf,
} from './backend/IRenderer'
import { App } from '../../app'
import { undoRedoManager } from '../../app/undoRedo'
import { useAllTaskStore } from '../../app/store/task'
import { EntityFactory } from './entity/factory'

export class CanvasEngineHandleController extends CanvasEngineGrabController {
  // EventManager 인스턴스
  protected eventManager = EventManager.getInstance()

  // EntityFactory 인스턴스 (lazy initialization)
  private _entityFactory?: EntityFactory
  protected get entityFactory(): EntityFactory {
    if (!this._entityFactory) {
      this._entityFactory = new EntityFactory(this)
    }
    return this._entityFactory
  }

  // 스페이스바 패닝 상태
  protected isSpacePanning: boolean = false
  private spacePanStartPos: { x: number; y: number } = { x: 0, y: 0 }
  private spacePanInitialStagePos: { x: number; y: number } = { x: 0, y: 0 }

  // 방향 도형 미리보기 데이터
  // lines·arrow·tempArrow는 stateManager helper가 렌더러 팩토리로 만들고 갱신한다
  private directionPreviewData: {
    lines: ILine[] // 선분들
    arrow?: IArrow // 화살표 머리
    tempArrow?: IArrow // 임시 화살표 (방향 결정 전)
    snapGuideLines: ILine[] // 스냅 가이드라인들
  } = {
    lines: [],
    snapGuideLines: [],
  }

  // DIRECTION 타입 돋보기 상태 (단순화)
  public directionDrawState = {
    showMagnifyingGlass: false, // 돋보기 표시 상태
    currentX: 0,
    currentY: 0,
    magPosition: 'top', // top, bottom, left, right
  }

  // 돋보기(렌더러 capability). 위치 정책·복제 대상 판정은 엔진에 남긴다
  private magnifier: IMagnifierBackend | null = null

  // 돋보기 상태
  private magnifierState: MagnifierState = {
    visible: false,
    x: 0,
    y: 0,
    position: 'top',
  }

  // Entity 상호작용 모드 (ICanvasEngineEntityController 인터페이스 구현을 위한 추가)
  __entityInteractionMode: boolean = true

  // 선택 영역 그리기를 위한 변수들

  /**
   * Entity 상호작용 모드를 반환합니다.
   * @returns Entity 상호작용 가능 여부
   */
  getEntityInteractionMode(): boolean {
    return this.__entityInteractionMode
  }

  /**
   * Entity 상호작용 모드를 설정합니다.
   * @param mode Entity 상호작용 가능 여부
   */
  setEntityInteractionMode(mode: boolean): void {
    this.__entityInteractionMode = mode
  }

  /**
   * 뷰포트 데이터를 직접 설정합니다.
   * @param x - 뷰포트의 x 좌표
   * @param y - 뷰포트의 y 좌표
   * @param zoom - 줌 레벨 (1.0이 기본 크기)
   * @returns Promise<boolean> - 설정 성공 여부
   */
  async setViewport(x: number, y: number, zoom: number): Promise<boolean> {
    const stage = this.renderer.stage
    const minZoom = this.getMinZoom()
    const maxZoom = this.getMaxZoom()

    // 줌 레벨 검증
    if (zoom < minZoom || zoom > maxZoom) {
      console.warn('줌 레벨이 허용 범위를 벗어났습니다:', { zoom, minZoom, maxZoom })
      return false
    }

    try {
      // 줌 레벨 설정
      stage.scale({ x: zoom, y: zoom })

      // 위치 설정
      stage.position({ x, y })
      if (this.tileLayer) this.handleViewportChange()
      this.renderer.requestDraw()
      return true
    } catch (error) {
      console.error('뷰포트 설정 중 오류 발생:', error)
      return false
    }
  }

  /**
   * 뷰포트를 애니메이션으로 이동합니다 (경계 API — 바깥에서 Konva.Tween을 만들지 않도록).
   * setViewport와 달리 줌 범위를 검증하지 않는다(호출자가 getMinZoom/getMaxZoom으로 맞춘 값을 넘긴다).
   * 타일 레이어 갱신·onViewportChanged 발행은 하지 않는다(기존 스토어 fitView 동작 유지). 필요하면 onFinish에서 처리.
   */
  animateViewport(
    x: number,
    y: number,
    zoom: number,
    options: { duration?: number; easing?: 'easeInOut' | 'easeOut'; onFinish?: () => void } = {},
  ): void {
    const tween = this.renderer.tween(
      this.renderer.stage,
      { x, y, scaleX: zoom, scaleY: zoom },
      {
        duration: options.duration ?? 0.2,
        easing: options.easing === 'easeOut' ? 'easeOut' : 'easeInOut',
        onFinish: (): void => {
          options.onFinish?.()
        },
      },
    )
    tween.play()
  }

  changeCanvasHandleMode(mode: CANVAS_HANDLE_MODE): void {
    // 모드 변경 시 항상 방향도형 미리보기, 돋보기 정리 및 그랩 모드 종료
    this.directionPreviewDestroy()
    this.destroyMagnifier()
    this.exitGrabMode()
    this.cleanupEraserCursor()

    super.changeCanvasHandleMode(mode)
    this.restoreEntitiyListening()
    if (this.canvasHandleMode == CANVAS_HANDLE_MODE.DRAW) {
      this.blurAllEntity()
      this.addSpacePanEvent()
      if (this.isEraserMode) {
        this.addBrushEraserEvent()
      } else if (this.brushDrawMode === 'free') {
        this.addBrushDrawEvent()
      } else {
        this.addBrushShapeDragEvent()
      }
      this.addWheelZoomEvent()
      this.addPinchZoomEvent()
      this.disableEntityListening()
    } else if (this.canvasHandleMode == CANVAS_HANDLE_MODE.DIRECTION_DRAW) {
      this.blurAllEntity()
      this.addDirectionDrawEvent()
      this.addSpacePanEvent()
      this.addWheelZoomEvent()
      this.addPinchZoomEvent()
      this.disableEntityListening()
    } else if (this.canvasHandleMode == CANVAS_HANDLE_MODE.DEFAULT) {
      this.addDefaultSelectorEvent()
      this.addWheelZoomEvent()
      this.addPinchZoomEvent()
    } else if (this.canvasHandleMode == CANVAS_HANDLE_MODE.CREATE) {
      this.initStageEvent()
      switch (this.createMode) {
        case CANVAS_CREATE_METHOD.DRAG:
          this.createShapeDragEvent()
          break
        case CANVAS_CREATE_METHOD.DRAG_AND_DROP:
          this.createShapeDragAndDropEvent()
          break
        case CANVAS_CREATE_METHOD.MOUSE_UP:
          this.createShapeMouseUpEvent()
          this.disableEntityListening()
          break
      }
      this.addSpacePanEvent()
      this.addWheelZoomEvent()
      this.addPinchZoomEvent()
    }
    // 자동 정렬 모드인 경우에도 줌 가능
    else if (this.canvasHandleMode == CANVAS_HANDLE_MODE.AUTO_ALIGN) {
      this.addWheelZoomEvent()
      this.addPinchZoomEvent()
    }
  }

  /**
   * 기본 stage에 조작할 때 이벤트
   * layerCache일 때는 작동하지 않아서 선택자로 사용하지 않음.
   */
  protected addDefaultSelectorEvent(): void {
    const stage = this.renderer.stage
    if (!stage) return

    // 스페이스바 패닝 이벤트
    this.addSpacePanEvent()

    // 그랩 이벤트 추가
    this.addGrabBoxEvents()

    const defaultSelectorEvents = `${CANVAS_HANDLE_EVENT.DEFAULT_MODE_CLICK} ${CANVAS_HANDLE_EVENT.DEFAULT_MODE_TAP}`
    this.eventManager.addEventFromString(
      stage,
      defaultSelectorEvents,
      (e: PointerEventOf<Event>) => {
        // 스페이스바 패닝 중이면 무시
        if (this.isSpacePressed) return
        // shift+클릭은 다중 선택 처리이므로 blur 방지
        if (e.evt instanceof MouseEvent && e.evt.shiftKey) return
        // 엔티티를 클릭하거나 핀치줌에서는 경우는 제외
        if (e.target !== stage || this.isPinchZooming) return
        // 애니메이션 중 합성 click 이벤트로 선택이 해제되는 것을 방지
        if (this.isAnimating) return
        this.blurAllEntity()
      },
    )
  }

  // 스페이스바 패닝 이벤트
  protected addSpacePanEvent(): void {
    // 앱환경에서는 비활성화
    if (this.isMobile) return
    if (typeof window === 'undefined') return

    const stage = this.renderer.stage
    if (!stage) return

    // 스페이스바 keydown/keyup
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.code === 'Space' && !this.isSpacePressed) {
        // input, textarea 등에서는 무시
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return

        e.preventDefault()
        this.isSpacePressed = true
        this.updateCursorForSpacePan()
      }
    })

    window.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        this.isSpacePressed = false
        this.isSpacePanning = false
        this.resumeLayerHitDetection()
        this.updateCursorForSpacePan()
      }
    })

    window.addEventListener('blur', () => {
      this.isSpacePressed = false
      this.isSpacePanning = false
      this.resumeLayerHitDetection()
      this.updateCursorForSpacePan()
    })

    // 스페이스바 패닝 마우스 이벤트
    this.eventManager.addEvent(stage, 'mousedown', (e: PointerEventOf<MouseEvent>) => {
      if (this.isSpacePressed && e.evt.button === 0) {
        e.evt.preventDefault()
        e.evt.stopPropagation()
        const stage = this.renderer.stage
        this.isSpacePanning = true
        // 팬 중에는 hit 캔버스 렌더 생략 (종료 경로: mouseup/keyup/blur에서 복원)
        this.suspendLayerHitDetection()
        this.spacePanStartPos = {
          x: e.evt.clientX,
          y: e.evt.clientY,
        }
        this.spacePanInitialStagePos = {
          x: stage.x(),
          y: stage.y(),
        }
        this.updateCursorForSpacePan()
      }
    })

    this.eventManager.addEvent(stage, 'mousemove', (e: PointerEventOf<MouseEvent>) => {
      if (this.isSpacePanning) {
        e.evt.preventDefault()
        e.evt.stopPropagation()
        const stage = this.renderer.stage
        const dx = e.evt.clientX - this.spacePanStartPos.x
        const dy = e.evt.clientY - this.spacePanStartPos.y

        stage.position({
          x: this.spacePanInitialStagePos.x + dx,
          y: this.spacePanInitialStagePos.y + dy,
        })

        this.renderer.requestDraw()
      }
    })

    this.eventManager.addEvent(stage, 'mouseup', (e: PointerEventOf<MouseEvent>) => {
      if (this.isSpacePanning && e.evt.button === 0) {
        e.evt.preventDefault()
        e.evt.stopPropagation()
        this.isSpacePanning = false
        this.resumeLayerHitDetection()
        this.updateCursorForSpacePan()
      }
    })
  }

  /**
   * 스페이스바 패닝 커서 업데이트
   */
  private updateCursorForSpacePan(): void {
    const stage = this.renderer.stage
    if (!stage) return
    const container = stage.container()
    if (this.isSpacePanning) container.style.cursor = 'grabbing'
    else if (this.isSpacePressed) container.style.cursor = 'grab'
    else container.style.cursor = 'default'
  }

  /**
   * 휠 줌 이벤트
   */
  protected addWheelZoomEvent(): void {
    const stage = this.renderer.stage

    const minZoom = this.getMinZoom()
    const maxZoom = this.getMaxZoom()

    this.eventManager.removeEvent(stage, CANVAS_HANDLE_EVENT.DEFAULT_MODE_WHEEL)
    this.eventManager.addEvent(
      stage,
      CANVAS_HANDLE_EVENT.DEFAULT_MODE_WHEEL,
      (e: PointerEventOf<WheelEvent>) => {
        const pointerPosition = this.renderer.getPointerPosition()
        if (!pointerPosition) return
        const scaleBy = 1.05
        e.evt.preventDefault()
        // 휠 줌 중 hit 렌더 생략 + 브러시 일괄 캐시 (디바운스 종료 시 복원)
        this.beginWheelGesture()
        const oldScale = stage.scaleX()

        const mousePointTo = {
          x: pointerPosition.x / oldScale - stage.x() / oldScale,
          y: pointerPosition.y / oldScale - stage.y() / oldScale,
        }

        const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy

        if (newScale < minZoom || newScale > maxZoom) return

        stage.scale({ x: newScale, y: newScale })

        const newPos = {
          x: -(mousePointTo.x - pointerPosition.x / newScale) * newScale,
          y: -(mousePointTo.y - pointerPosition.y / newScale) * newScale,
        }

        stage.position(newPos)
        this.renderer.requestDraw()
      },
    )
  }

  /**
   * 핀치 줌 이벤트
   */
  public currentTouchCount = 0

  // 캔버스 터치 ID 추적
  private canvasTouchIds: Set<number> = new Set()

  // 터치 개수 추적
  private setupTouchTracking(): void {
    const stage = this.renderer.stage

    this.eventManager.removeEvent(stage, 'touchstart')
    this.eventManager.addEvent(stage, 'touchstart', (e: PointerEventOf<TouchEvent>) => {
      // stale 터치 ID 자기 치유 — touchend/touchcancel이 stage에 전달되지 못한 경우
      // 잔존 ID가 핀치줌 오인(한 손가락도 2개로 계산)을 일으키므로 실존 터치 목록과 대조해 제거
      const activeTouchIds = new Set<number>()
      for (let i = 0; i < e.evt.touches.length; i++) {
        activeTouchIds.add(e.evt.touches[i].identifier)
      }
      let removedStale = false
      for (const trackedId of [...this.canvasTouchIds]) {
        if (!activeTouchIds.has(trackedId)) {
          this.canvasTouchIds.delete(trackedId)
          removedStale = true
          console.warn(`[CanvasEngine] stale touch id 제거: ${trackedId}`)
        }
      }
      // stale 제거로 추적 터치가 0이 되었다면 누락된 touchend를 보정 —
      // 고착된 핀치 상태를 풀고 엔티티 인터랙션을 복원해 이번 터치가 정상 동작하게 함
      if (removedStale && this.canvasTouchIds.size === 0 && this.isPinchZooming) {
        this.isPinchZooming = false
        this.restoreEntityInteractions()
      }

      // 새로 시작된 터치들만 확인
      const changedTouches = e.evt.changedTouches
      for (let i = 0; i < changedTouches.length; i++) {
        const touch = changedTouches[i]
        const targetElement = document.elementFromPoint(touch.clientX, touch.clientY)

        // CANVAS 태그를 터치한 경우만 추적
        if (targetElement && targetElement.tagName === 'CANVAS') {
          this.canvasTouchIds.add(touch.identifier)
        }
      }

      this.currentTouchCount = this.canvasTouchIds.size
      if (this.currentTouchCount >= 2) {
        this.isPinchZooming = true
        // 첫 손가락이 거의 안 움직인 채 둘째 손가락이 닿으면 스테이지 드래그(한 손가락 팬)가 '시작 대기'로 남아,
        // 핀치 도중 뒤늦게 시작하며 첫 손가락이 닿던 때의 위치로 화면을 한 프레임 끌어당긴다(실기기 손가락 핀치 72회 중 23회,
        // 50~750px 튐 → 다음 핀치 계산이 되돌림). 대기 드래그까지 지금 취소한다
        this.renderer.stage.cancelDrag()
        // 핀치 중에는 hit 캔버스 렌더 생략 (프레임당 렌더 비용 절반 수준으로 절감)
        this.suspendLayerHitDetection()
        // 즉시 모든 드래그 중단
        this.stopAllDrags()

        // 도형 생성 중이면 취소
        if (this.__isEntitiyCreating) {
          this.__isEntitiyCreating = false
          // 가이드라인 정리
          this.__createGuideLines.forEach(line => line.destroy())
          this.__createGuideLines = []
        }
      }
    })

    this.eventManager.removeEvent(stage, 'touchend')
    this.eventManager.addEvent(stage, 'touchend', (e: PointerEventOf<TouchEvent>) => {
      // 종료된 터치들 제거
      const changedTouches = e.evt.changedTouches
      for (let i = 0; i < changedTouches.length; i++) {
        this.canvasTouchIds.delete(changedTouches[i].identifier)
      }

      this.currentTouchCount = this.canvasTouchIds.size
      // 모든 터치가 종료되었을 때만 상태 복원
      if (this.currentTouchCount === 0) {
        this.isPinchZooming = false
        this.restoreEntityInteractions()
      }
    })

    // touchcancel 이벤트 처리
    this.eventManager.removeEvent(stage, 'touchcancel')
    this.eventManager.addEvent(stage, 'touchcancel', (e: PointerEventOf<TouchEvent>) => {
      // 취소된 터치들 제거
      const changedTouches = e.evt.changedTouches
      for (let i = 0; i < changedTouches.length; i++) {
        this.canvasTouchIds.delete(changedTouches[i].identifier)
      }

      this.currentTouchCount = this.canvasTouchIds.size
      if (this.currentTouchCount === 0) {
        this.isPinchZooming = false
        this.restoreEntityInteractions()
      }
    })
  }

  private stopAllDrags(): void {
    // 원래 여기서 stage.find('*')로 "모든 노드 드래그 중단 + draggable(false)"를 하려 했으나 Konva에서 '*'는
    // 빈 배열이라 실제로는 아무것도 하지 않았다. 동작 보존을 위해
    // 그 no-op 루프를 지웠다 — renderer.stopAllDrags()(전 노드 드래그 중단)로 바꾸는 것은 별도 결정.

    // Entity 드래그 중단
    this.entities.forEach(entity => {
      if (entity.node.isDragging && entity.node.isDragging()) {
        entity.node.stopDrag()
      }
      entity.node.draggable(false)
      entity.node.listening(false)

      // balloon 드래그 중단 및 위치 동기화
      if (entity.balloon) {
        const wasBalloonDragging = entity.balloon.head.rect.isDragging()
        if (wasBalloonDragging) {
          entity.balloon.head.rect.stopDrag()
        }
        entity.balloon.head.rect.draggable(false)
        entity.balloon.head.text.draggable(false)

        // balloon이 드래그 중이었다면 transformer 위치 동기화 필요
        if (wasBalloonDragging && entity.isSelected) {
          const transformer = entity.getShapeTransformer()
          if (transformer) {
            transformer.forceUpdate()
          }
        }
      }

      if (entity.isSelected) {
        entity.transformerDragable(false)

        // shape 드래그 중이었다면 transformer 동기화
        const shape = entity.getShape()
        if (shape && shape.isDragging()) {
          shape.stopDrag()
          const transformer = entity.getShapeTransformer()
          if (transformer) {
            transformer.forceUpdate()
          }
        }
      }

      // 방향도형의 경우 centerPoint도 드래그 중단
      if (entity.isDirection()) {
        const shape = entity.getShape()
        if (shape && shape.isDragging()) {
          shape.stopDrag()
        }
      }
    })
  }

  private restoreEntityInteractions(): void {
    // 핀치 종료 공통 경로 — 조작 중 생략한 hit 캔버스 렌더 복원
    this.resumeLayerHitDetection()
    const interactionEnabled = this.getEntityInteractionMode() && !this.isPinchZooming

    this.entities.forEach(entity => {
      entity.node.listening(true)
      const canDrag = interactionEnabled && entity.isSelected

      entity.node.draggable(canDrag)
      if (entity.balloon) {
        entity.balloon.head.rect.draggable(canDrag)
        entity.balloon.head.text.draggable(canDrag)
      }
      if (entity.isSelected) {
        entity.transformerDragable(canDrag)

        // 선택된 entity의 transformer만 개별적으로 복원 (entity 타입에 맞게)
        const transformer = entity.getShapeTransformer()
        if (transformer) {
          transformer.draggable(canDrag)

          // POINT는 회전/리사이즈 비활성화 유지
          if (entity.isPoint()) {
            transformer.config({ rotateEnabled: false, resizeEnabled: false })
          }
          // DIRECTION은 회전만 활성화
          else if (entity.isDirection()) {
            transformer.config({ rotateEnabled: canDrag, resizeEnabled: false })
          }
          // TRIPOD는 회전만 활성화
          else if (entity.isTripod()) {
            transformer.config({ rotateEnabled: canDrag, resizeEnabled: false })
          }
          // 나머지는 둘 다 활성화
          else {
            transformer.config({ rotateEnabled: canDrag, resizeEnabled: canDrag })
          }

          // transformer 위치 동기화
          transformer.forceUpdate()
        }
      }
    })

    // 시스템 레이어 다시 그리기
    const systemLayer = this.renderer.layers.system
    if (systemLayer) {
      systemLayer.batchDraw()
    }
  }

  protected addPinchZoomEvent(): void {
    this.setupTouchTracking()

    const stage = this.renderer.stage
    const minZoom = this.getMinZoom()
    const maxZoom = this.getMaxZoom()
    const setState = this.setState

    let lastCenter: Vector2d | undefined = undefined
    let lastDist = 0
    let smoothedDist = 0
    const SMOOTHING_FACTOR = 0.4
    let animationFrameId: number | null = null

    this.eventManager.removeEvent(stage, CANVAS_HANDLE_EVENT.PINCH_ZOOM_TOUCH_MOVE)
    this.eventManager.addEvent(stage, CANVAS_HANDLE_EVENT.PINCH_ZOOM_TOUCH_MOVE, e => {
      // 캔버스 터치만 필터링
      const canvasTouches: Touch[] = []
      for (let i = 0; i < e.evt.touches.length; i++) {
        const touch = e.evt.touches[i]
        if (this.canvasTouchIds.has(touch.identifier)) {
          canvasTouches.push(touch)
        }
      }

      // 핀치 줌 상태를 확인하기 위해 canvas 터치가 두개 이상이어야함
      if (canvasTouches.length < 2) {
        return
      }
      e.evt.preventDefault()

      // 멀티터치 중에는 도형 생성 취소
      if (this.__isEntitiyCreating) {
        this.__isEntitiyCreating = false
        this.__createGuideLines.forEach(line => line.destroy())
        this.__createGuideLines = []
      }

      const touch1 = canvasTouches[0]
      const touch2 = canvasTouches[1]

      if (!touch1 || !touch2) return
      // 진행 중이든 시작 대기든 스테이지 드래그 취소(위 touchstart 주석 — 대기 드래그는 isDragging()이 false)
      stage.cancelDrag()

      const p1 = {
        x: touch1.clientX,
        y: touch1.clientY,
      }
      const p2 = {
        x: touch2.clientX,
        y: touch2.clientY,
      }

      const newCenter = getCenter(p1, p2)
      const dist = getDistance(p1, p2)

      // 초기화
      if (!lastCenter || !lastDist) {
        lastCenter = newCenter
        lastDist = dist
        smoothedDist = dist
        return
      }

      // Exponential Moving Average로 거리 스무딩
      smoothedDist = smoothedDist * (1 - SMOOTHING_FACTOR) + dist * SMOOTHING_FACTOR

      // local coordinates of center point
      const pointTo = {
        x: (newCenter.x - stage.x()) / stage.scaleX(),
        y: (newCenter.y - stage.y()) / stage.scaleX(),
      }

      // 스무딩된 거리로 스케일 계산 — 범위를 벗어나면 버리지 않고 경계값으로 클램프
      // (저사양에서 빠른 핀치는 이벤트가 드물고 변화폭이 커서, 버리면 "안 움직임"이 됨)
      const rawScale = stage.scaleX() * (smoothedDist / lastDist)
      const scale = Math.min(maxZoom, Math.max(minZoom, rawScale))

      // 중심점 이동 계산 (패닝)
      const dx = newCenter.x - lastCenter.x
      const dy = newCenter.y - lastCenter.y

      // 새로운 위치 계산 (줌 + 패닝)
      const newPos = {
        x: newCenter.x - pointTo.x * scale + dx,
        y: newCenter.y - pointTo.y * scale + dy,
      }

      // 랜더링 취소
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId)

      animationFrameId = requestAnimationFrame(() => {
        setState(CANVAS_STATE.ZOOMING)
        stage.setViewTransform(scale, newPos)
        this.renderer.requestDraw()
        animationFrameId = null
      })

      lastDist = smoothedDist
      lastCenter = newCenter
    })

    this.eventManager.removeEvent(stage, CANVAS_HANDLE_EVENT.PINCH_ZOOM_TOUCH_END)
    this.eventManager.addEvent(stage, CANVAS_HANDLE_EVENT.PINCH_ZOOM_TOUCH_END, () => {
      // 대기 중인 렌더링 취소
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }

      lastDist = 0
      lastCenter = undefined
      smoothedDist = 0
      setState(CANVAS_STATE.IDLE)

      // 핀치 줌 종료 후 선택된 entity의 transformer 위치 동기화
      this.entities.forEach(entity => {
        if (entity.isSelected) {
          const transformer = entity.getShapeTransformer()
          if (transformer) {
            // transformer의 nodes가 유효한지 확인
            const nodes = transformer.nodes()
            if (nodes && nodes.length > 0) {
              transformer.forceUpdate()
            }
          }
        }
      })

      const systemLayer = this.renderer.layers.system
      if (systemLayer) {
        systemLayer.batchDraw()
      }

      // 타일 레이어 업데이트 (핀치 줌 완전 종료 후)
      if (this.tileLayer && !this.isPinchZooming) {
        this.handleViewportChange()
      }
    })
  }

  // 방향도형 미리보기 정리 함수
  private directionPreviewDestroy(): void {
    // tempArrow
    if (this.directionPreviewData.tempArrow) {
      this.directionPreviewData.tempArrow.destroy()
      this.directionPreviewData.tempArrow = undefined
    }

    // 선분들
    if (this.directionPreviewData.lines.length > 0) {
      this.directionPreviewData.lines.forEach(line => line.destroy())
      this.directionPreviewData.lines.length = 0 // 배열 비우기 최적화
    }

    // 화살표 머리
    if (this.directionPreviewData.arrow) {
      this.directionPreviewData.arrow.destroy()
      this.directionPreviewData.arrow = undefined
    }

    // 스냅 가이드라인들
    if (this.directionPreviewData.snapGuideLines.length > 0) {
      this.directionPreviewData.snapGuideLines.forEach(line => line.destroy())
      this.directionPreviewData.snapGuideLines.length = 0
    }
  }

  /**
   * 돋보기를 초기화합니다(레이어는 렌더러 capability가 첫 내용 갱신·표시 때 현재 줌의 역배율로 만든다).
   */
  private initializeMagnifier(): void {
    // 기존 돋보기 구성 요소 정리
    this.destroyMagnifier()

    this.magnifier =
      this.renderer.capabilities.magnifier?.({
        radius: MAGNIFIER_RADIUS,
        scale: MAGNIFIER_SCALE,
        borderWidth: MAGNIFIER_BORDER_WIDTH,
        borderColor: MAGNIFIER_BORDER_COLOR,
      }) ?? null
  }

  /**
   * 돋보기 구성 요소를 정리합니다.
   */
  private destroyMagnifier(): void {
    this.magnifier?.destroy()
    this.magnifier = null

    // 상태 초기화
    this.magnifierState = {
      visible: false,
      x: 0,
      y: 0,
      position: 'top',
    }
  }

  /**
   * 포인터 위치를 기준으로 돋보기 배치 위치를 계산합니다.
   * @param pointerX 포인터의 x 좌표
   * @param pointerY 포인터의 y 좌표
   * @returns 돋보기 위치와 배치 방향
   */
  private calculateMagnifierPosition(
    pointerX: number,
    pointerY: number,
  ): {
    x: number
    y: number
    position: MagnifierPosition
    viewportWidth: number
    viewportHeight: number
  } {
    const stage = this.renderer.stage
    const scale = stage.scaleX()
    const stagePos = stage.position()

    // 뷰포트 크기 계산
    const viewportWidth = stage.width() / scale
    const viewportHeight = stage.height() / scale
    const viewportX = -stagePos.x / scale
    const viewportY = -stagePos.y / scale

    // 포인터가 뷰포트 경계에서 얼마나 떨어져 있는지 계산
    const distanceFromLeft = pointerX - viewportX
    const distanceFromRight = viewportX + viewportWidth - pointerX
    const distanceFromTop = pointerY - viewportY
    const distanceFromBottom = viewportY + viewportHeight - pointerY

    let position: MagnifierPosition = 'top'
    let magnifierX = pointerX
    let magnifierY = pointerY

    // 로컬 좌표계 기준 고정 거리
    const offsetDistance = (MAGNIFIER_RADIUS + MAGNIFIER_OFFSET) / scale

    if (distanceFromLeft <= MAGNIFIER_VIEWPORT_MARGIN) {
      // 1. 뷰포트 왼쪽 경계에 50px 이하면 포인터 우측에 표시
      position = 'right'
      magnifierX = pointerX + offsetDistance
    } else if (distanceFromRight <= MAGNIFIER_VIEWPORT_MARGIN) {
      // 2. 뷰포트 오른쪽 경계에 50px 이하면 포인터 좌측에 표시
      position = 'left'
      magnifierX = pointerX - offsetDistance
    } else if (distanceFromTop <= MAGNIFIER_VIEWPORT_MARGIN) {
      // 3. 뷰포트 위쪽 경계에 50px 이하면 포인터 하단에 표시
      position = 'bottom'
      magnifierY = pointerY + offsetDistance
    } else if (distanceFromBottom <= MAGNIFIER_VIEWPORT_MARGIN) {
      // 4. 뷰포트 아래쪽 경계에 50px 이하면 포인터 위쪽에 표시
      position = 'top'
      magnifierY = pointerY - offsetDistance
    } else {
      // 5. 그 외 모든 경우에는 포인터 위쪽에 표시
      position = 'top'
      magnifierY = pointerY - offsetDistance
    }

    return { x: magnifierX, y: magnifierY, position, viewportWidth, viewportHeight }
  }

  /**
   * 돋보기를 표시합니다.
   * @param pointerX 포인터의 x 좌표 (돋보기 위치)
   * @param pointerY 포인터의 y 좌표 (돋보기 위치)
   * @param focusX 확대할 중심 x 좌표 (미지정시 pointerX 사용)
   * @param focusY 확대할 중심 y 좌표 (미지정시 pointerY 사용)
   */
  private showMagnifier(
    pointerX: number,
    pointerY: number,
    focusX?: number,
    focusY?: number,
  ): void {
    if (!this.magnifier) {
      this.initializeMagnifier()
    }

    if (!this.magnifier) {
      return
    }

    // 위치 계산
    const { x, y, position } = this.calculateMagnifierPosition(pointerX, pointerY)

    // 상태 업데이트
    this.magnifierState.visible = true
    this.magnifierState.x = x
    this.magnifierState.y = y
    this.magnifierState.position = position

    // 돋보기 표시(구성 요소 위치는 현재 줌을 곱해 화면 고정 크기로)
    this.magnifier.show({
      x,
      y,
      focusX: focusX !== undefined ? focusX : pointerX,
      focusY: focusY !== undefined ? focusY : pointerY,
    })
  }

  /**
   * 돋보기를 숨깁니다.
   */
  private hideMagnifier(): void {
    this.magnifier?.hide()
    this.magnifierState.visible = false
  }

  /**
   * 돋보기 내용을 업데이트합니다. (메인 캔버스 내용을 캡처)
   */
  private updateMagnifierContent(): void {
    if (!this.magnifier) {
      return
    }

    // 보이는 레이어의 자식 노드를 복제한다(돋보기 레이어 자체는 구현체가 제외).
    // SystemLayer는 direction preview를 위해 포함하되 그 외 시스템 요소는 제외
    const systemLayer = this.renderer.layers.system
    // 엔티티 루트는 복제하지 않는다 — 상속 시절 엔티티(Konva.Group 서브클래스)의 clone()은
    // `new this.constructor(attrs)`가 엔티티 생성자 인자와 맞지 않아 항상 실패해 돋보기에서 빠졌다(동작 보존)
    const entityNodes = new Set<INode>(this.entities.map(entity => entity.node))
    this.magnifier.refreshContent((node: INode, layer): boolean => {
      if (entityNodes.has(node)) return false
      const nodeId = node.id()
      if (layer === systemLayer) {
        // Direction preview 요소만 포함하고 나머지는 제외
        const isDirectionPreview =
          node.kind === 'Line' ||
          node.kind === 'Arrow' ||
          (nodeId && (nodeId.includes('direction') || nodeId.includes('temp')))

        // Direction preview가 아닌 시스템 요소들은 제외
        return !(
          !isDirectionPreview ||
          node.kind === 'Transformer' ||
          nodeId.includes('guide') ||
          nodeId.includes('selection') ||
          nodeId.includes('single-selection')
        )
      }
      // 일반 레이어에서는 기존 로직 유지
      return !(
        node.kind === 'Transformer' ||
        nodeId.includes('temp') ||
        nodeId.includes('preview') ||
        nodeId.includes('guide') ||
        nodeId.includes('selection')
      )
    })
  }

  /**
   * 성능 최적화된 돋보기 위치 업데이트
   * @param pointerX 포인터의 x 좌표 (돋보기 위치)
   * @param pointerY 포인터의 y 좌표 (돋보기 위치)
   * @param focusX 확대할 중심 x 좌표 (미지정시 pointerX 사용)
   * @param focusY 확대할 중심 y 좌표 (미지정시 pointerY 사용)
   */
  private updateMagnifierPosition(
    pointerX: number,
    pointerY: number,
    focusX?: number,
    focusY?: number,
  ): void {
    if (!this.magnifierState.visible || !this.magnifier) return

    // 위치 계산
    const { x, y, position } = this.calculateMagnifierPosition(pointerX, pointerY)

    // 위치가 크게 변경되지 않았다면 업데이트 스킵 (성능 최적화)
    const positionThreshold = 5
    if (
      Math.abs(this.magnifierState.x - x) < positionThreshold &&
      Math.abs(this.magnifierState.y - y) < positionThreshold &&
      this.magnifierState.position === position
    ) {
      return
    }

    // 상태 업데이트
    this.magnifierState.x = x
    this.magnifierState.y = y
    this.magnifierState.position = position

    this.magnifier.show({
      x,
      y,
      focusX: focusX !== undefined ? focusX : pointerX,
      focusY: focusY !== undefined ? focusY : pointerY,
    })
  }

  // 다른 DIRECTION shape들의 꺾은선 점들을 가져오는 함수
  private getOtherDirectionBendPoints(): Vector2d[] {
    const bendPoints: Vector2d[] = []

    // canvasEngine에서 모든 엔티티 가져오기
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allEntities = (this as any).entities || []

    for (const entity of allEntities) {
      // DIRECTION 타입 엔티티만 처리
      if (
        entity.entityType === SHAPES.DIRECTION &&
        entity.__directionPoints &&
        entity.__directionPoints.length >= 4
      ) {
        const points = entity.__directionPoints

        // 모든 점을 Vector2d 형태로 변환하여 추가
        for (let i = 0; i < points.length; i += 2) {
          if (i + 1 < points.length) {
            bendPoints.push({
              x: points[i],
              y: points[i + 1],
            })
          }
        }
      }
    }

    return bendPoints
  }

  // 현재 끝점과 축 정렬된 꺾은선 점들 찾기 (범위 제한 적용)
  private findAlignedBendPoints(currentEndPoint: Vector2d): {
    horizontal: Vector2d[]
    vertical: Vector2d[]
  } {
    const bendPoints = this.getOtherDirectionBendPoints()
    const SNAP_THRESHOLD = 10 // 스냅 임계값
    const DETECTION_RANGE = 250 // 감지 범위 (끝점 기준 반경)

    const alignedPoints = {
      horizontal: [] as Vector2d[],
      vertical: [] as Vector2d[],
    }

    for (const bendPoint of bendPoints) {
      // 거리 체크 - 감지 범위 내에 있는 bend point만 처리
      const distance = Math.sqrt(
        Math.pow(currentEndPoint.x - bendPoint.x, 2) + Math.pow(currentEndPoint.y - bendPoint.y, 2),
      )

      if (distance > DETECTION_RANGE) {
        continue // 범위 밖의 bend point는 무시
      }

      // X축 정렬 체크 (세로 가이드라인)
      if (Math.abs(currentEndPoint.x - bendPoint.x) <= SNAP_THRESHOLD) {
        alignedPoints.vertical.push(bendPoint)
      }

      // Y축 정렬 체크 (가로 가이드라인)
      if (Math.abs(currentEndPoint.y - bendPoint.y) <= SNAP_THRESHOLD) {
        alignedPoints.horizontal.push(bendPoint)
      }
    }

    return alignedPoints
  }

  // 스냅 가이드라인 표시
  private showDirectionSnapGuides(
    _currentEndPoint: Vector2d,
    alignedPoints: { horizontal: Vector2d[]; vertical: Vector2d[] },
  ): void {
    this.clearDirectionSnapGuides()

    const systemLayer = this.renderer.layers.system
    if (!systemLayer) return

    const stage = this.renderer.stage

    // 현재 뷰포트 정보 계산 (balloon과 동일한 방식)
    const scale = stage.scaleX() || 1
    const viewportWidth = stage.width() / scale
    const viewportHeight = stage.height() / scale
    const viewportX = -stage.x() / scale
    const viewportY = -stage.y() / scale

    // X축 정렬된 점들이 있으면 세로 가이드라인 표시
    if (alignedPoints.vertical.length > 0) {
      const snapX = alignedPoints.vertical[0].x
      const verticalGuideLine = this.renderer.createLine({
        points: [snapX, viewportY, snapX, viewportY + viewportHeight],
        stroke: '#ff6b6b',
        strokeWidth: 2,
        dash: [8, 8],
        listening: false,
      })
      this.directionPreviewData.snapGuideLines.push(verticalGuideLine)
      systemLayer.add(verticalGuideLine)
    }

    // Y축 정렬된 점들이 있으면 가로 가이드라인 표시
    if (alignedPoints.horizontal.length > 0) {
      const snapY = alignedPoints.horizontal[0].y
      const horizontalGuideLine = this.renderer.createLine({
        points: [viewportX, snapY, viewportX + viewportWidth, snapY],
        stroke: '#ff6b6b',
        strokeWidth: 2,
        dash: [8, 8],
        listening: false,
      })
      this.directionPreviewData.snapGuideLines.push(horizontalGuideLine)
      systemLayer.add(horizontalGuideLine)
    }

    // 가이드라인 강제 렌더링
    systemLayer.batchDraw()
  }

  // 스냅 가이드라인 제거
  private clearDirectionSnapGuides(): void {
    if (this.directionPreviewData.snapGuideLines.length > 0) {
      this.directionPreviewData.snapGuideLines.forEach(line => line.destroy())
      this.directionPreviewData.snapGuideLines.length = 0
    }
  }

  // 줌 레벨에 따라 조정된 방향 고정 임계값을 반환
  private getDirDrawDirectionThreshold(): number {
    const factor = 1.5
    const threshold =
      DIR_DRAW_DIRECTION_THRESHOLD * (1 + (factor - 1) * (1 / this.renderer.stage.scaleX() - 1))
    return Math.max(threshold, DIR_DRAW_DIRECTION_THRESHOLD)
  }

  /**
   * SystemLayer를 완전히 초기화합니다.
   * 모든 transformer, 가이드라인, 미리보기 요소들을 제거합니다.
   */
  public clearSystemLayer(): void {
    try {
      const systemLayer = this.renderer.layers.system
      if (!systemLayer) return

      // 방향도형 미리보기 정리
      this.directionPreviewDestroy()

      // 돋보기 정리
      this.destroyMagnifier()

      // 그랩 모드 종료
      this.exitGrabMode()

      // 생성 가이드라인 정리
      if (this.__createGuideLines.length > 0) {
        this.__createGuideLines.forEach(line => line.destroy())
        this.__createGuideLines.length = 0
      }

      // SystemLayer의 모든 children 제거
      systemLayer.destroyChildren()

      // 강제 렌더링으로 변경사항 즉시 반영
      systemLayer.batchDraw()
    } catch (error) {
      console.error('SystemLayer 초기화 중 오류 발생:', error)
    }
  }

  // 스냅된 위치 계산
  private getSnappedEndPoint(
    currentEndPoint: Vector2d,
    alignedPoints: { horizontal: Vector2d[]; vertical: Vector2d[] },
  ): Vector2d {
    return {
      x: alignedPoints.vertical.length > 0 ? alignedPoints.vertical[0].x : currentEndPoint.x,
      y: alignedPoints.horizontal.length > 0 ? alignedPoints.horizontal[0].y : currentEndPoint.y,
    }
  }

  // 방향도형 그리기 이벤트
  protected addDirectionDrawEvent(): void {
    const stage = this.renderer.stage
    const setState = this.setState

    this.directionPreviewDestroy()

    // 그리기 상태 관리
    let isDrawing = false
    let startX = 0
    let startY = 0
    let drawingPath: number[] = []
    let isDirectionFixed = false
    let fixedDirection: (Vector2d & { index: number }) | null = null
    let fixedLines: DirectionLineType[] = []
    let activeLineIndex = 0
    let lastDirection: (Vector2d & { index: number }) | null = null // 마지막 방향성

    // 성능 최적화를 위한 렌더링 관리
    let renderAnimationId: number | null = null
    let lastRenderTime = 0
    const RENDER_THROTTLE_MS = 16 // ~60fps
    let pendingRenderPath: Vector2d[] | null = null

    // 화면에 그리기 함수 (requestAnimationFrame 사용)
    const updateDrawingRender = (path: Vector2d[]): void => {
      pendingRenderPath = [...path] // 경로 복사

      const now = performance.now()
      if (now - lastRenderTime < RENDER_THROTTLE_MS && renderAnimationId !== null) {
        return // 이미 예약된 렌더링이 있고 throttle 시간 내라면 스킵
      }

      if (renderAnimationId !== null) {
        cancelAnimationFrame(renderAnimationId)
      }

      renderAnimationId = requestAnimationFrame(() => {
        if (pendingRenderPath) {
          try {
            updateDirectionRender(
              this.renderer,
              pendingRenderPath,
              this.directionPreviewData.lines,
              this.directionPreviewData.arrow,
              fixedDirection,
              {
                startX,
                startY,
                layer: this.renderer.layers.system,
                color: this.forCreateBalloonBorderColor,
                strokeWidth: this.connectedLineWidth,
              },
            )
            lastRenderTime = performance.now()
          } catch (error) {
            console.warn('Direction render error:', error)
          }
        }
        renderAnimationId = null
        pendingRenderPath = null
      })
    }

    // 상태 초기화 함수
    const cleanupDirectionDrawing = (): void => {
      // 렌더링 애니메이션 정리
      if (renderAnimationId !== null) {
        cancelAnimationFrame(renderAnimationId)
        renderAnimationId = null
      }
      pendingRenderPath = null

      // 지역 상태 초기화
      isDrawing = false
      drawingPath = []
      isDirectionFixed = false
      fixedDirection = null
      fixedLines = []
      activeLineIndex = 0

      // 클래스 상태 초기화
      setState(CANVAS_STATE.IDLE)
      this.__isEntitiyCreating = false
      this.restoreEntitiyListening()

      // 미리보기 정리
      this.directionPreviewDestroy()

      // 돋보기 숨김
      if (this.magnifierState.visible) {
        this.hideMagnifier()
      }

      // 일괄 렌더링
      this.renderer.layers.system?.batchDraw()
    }

    const directionMouseDownEvents = `${CANVAS_HANDLE_EVENT.DIRECTION_DRAW_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.DIRECTION_DRAW_TOUCH_START}`
    const directionMouseMoveEvents = `${CANVAS_HANDLE_EVENT.DIRECTION_DRAW_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.DIRECTION_DRAW_TOUCH_MOVE}`
    const directionMouseUpEvents = `${CANVAS_HANDLE_EVENT.DIRECTION_DRAW_MOUSE_UP} ${CANVAS_HANDLE_EVENT.DIRECTION_DRAW_TOUCH_END}`

    this.eventManager.removeEventFromString(stage, directionMouseDownEvents)
    this.eventManager.removeEventFromString(stage, directionMouseMoveEvents)
    this.eventManager.removeEventFromString(stage, directionMouseUpEvents)

    // 미리보기 정리
    this.directionPreviewDestroy()

    // 마우스 다운 - 그리기 시작
    this.eventManager.addEventFromString(
      stage,
      directionMouseDownEvents,
      (e: PointerEventOf<PointerEvent | TouchEvent>) => {
        try {
          // 스페이스바 패닝 또는 핀치줌 중이면 무시
          if (this.isSpacePressed || this.isPinchZooming) return

          const taskStore = useAllTaskStore()
          const isDemoMode = App.Router.currentRoute.value.path.includes('/demo')
          if (taskStore && !isDemoMode && !taskStore.getDirectionDrawGuideVisible()) {
            App.toastInstance?.add('info', '드래그하여 방향에 맞게 화살표를 만들어주세요')
          }

          // 멀티터치 체크 개선
          let isMultiTouch = false
          try {
            if (e.evt instanceof TouchEvent && e.evt.touches) {
              isMultiTouch = e.evt.touches.length > 1
            }
          } catch (_error) {
            // 터치 이벤트 체크 실패 시 무시
          }

          // 이미 그리는 중이거나 멀티터치면 무시
          if (isDrawing || (this.isApp && isMultiTouch) || this.__isCreateCallbackPending) return

          this.__isEntitiyCreating = true

          // preventDefault 안전하게 처리
          try {
            e.evt.preventDefault()
          } catch (_error) {
            // Safari에서 preventDefault 실패할 수 있음 - 무시
          }

          let pos: Vector2d
          try {
            pos = this.getPointerPosition()
          } catch (_error) {
            console.warn('Failed to get pointer position in mousedown:', _error)
            cleanupDirectionDrawing()
            return
          }

          const systemLayer = this.renderer.layers.system

          isDrawing = true
          startX = pos.x
          startY = pos.y
          drawingPath = [pos.x, pos.y]
          isDirectionFixed = false
          fixedDirection = null

          // 그리기 시작 시 임시 화살표 생성
          const defaultDirection = lastDirection || { x: 0, y: -1, index: 1 } // 북쪽 방향
          this.directionPreviewData.tempArrow = createTempArrow(this.renderer, defaultDirection, {
            startX,
            startY,
            color: this.forCreateBalloonBorderColor,
            strokeWidth: this.connectedLineWidth,
          })

          if (systemLayer) systemLayer.add(this.directionPreviewData.tempArrow)

          // 방향 그리기 시작 시 돋보기 표시
          this.destroyMagnifier() // 기존 돋보기 완전히 정리
          this.initializeMagnifier() // 새로 초기화
          this.updateMagnifierContent()
          this.showMagnifier(pos.x, pos.y, startX, startY)

          setState(CANVAS_STATE.DRAWING)
          this.__isEntitiyCreating = true
          this.disableEntityListening()
        } catch (error) {
          cleanupDirectionDrawing()
          console.error('directionMouseDownEvents error:', error)
        }
      },
    )

    // 마우스 무브 - 그리기 중
    this.eventManager.addEventFromString(
      stage,
      directionMouseMoveEvents,
      (e: PointerEventOf<PointerEvent | TouchEvent>) => {
        try {
          if (!isDrawing || this.isPinchZooming) return

          // 브라우저별 이벤트 처리 개선
          try {
            e.evt.preventDefault()
          } catch (_error) {
            // Safari에서 preventDefault 실패할 수 있음 - 무시
          }

          let pos: Vector2d
          try {
            pos = this.getPointerPosition()
          } catch (_error) {
            console.warn('Failed to get pointer position:', _error)
            return
          }

          // 경로에 점 추가 (스냅 적용 전 원본 위치)
          drawingPath.push(pos.x, pos.y)

          // 현재 드래그 거리 계산
          const currentDistance = Math.sqrt((pos.x - startX) ** 2 + (pos.y - startY) ** 2)

          // 돋보기 표시/숨김 로직
          if (!isDirectionFixed && currentDistance <= DIR_DRAW_MAGNIFYING_GLASS_THRESHOLD) {
            // 특정 길이 이하이고 방향 고정 전이면 돋보기 표시 (시작점 확대)
            if (!this.magnifierState.visible) {
              // 돋보기가 없으면 다시 생성
              if (!this.magnifier) {
                this.initializeMagnifier()
              }
              this.updateMagnifierContent() // 돋보기 내용 업데이트
              this.showMagnifier(pos.x, pos.y, startX, startY)
            } else {
              // 이미 표시된 상태라면 내용과 위치 모두 업데이트 (direction shape 실시간 반영)
              this.updateMagnifierContent() // direction preview 변화 반영
              this.updateMagnifierPosition(pos.x, pos.y, startX, startY)
            }
          } else {
            // 방향 고정 후이거나 특정 길이 이상이면 돋보기 숨김
            if (this.magnifierState.visible) {
              this.hideMagnifier()
            }
          }

          // 사용자 가이드 토스트 (돋보기 거리 기반)
          const taskStore = useAllTaskStore()
          const isDemoMode = App.Router.currentRoute.value.path.includes('/demo')
          if (
            taskStore &&
            !isDemoMode &&
            currentDistance > 30 &&
            !taskStore.getDirectionDrawGuideVisible()
          ) {
            App.toastInstance?.add('info', '원하는 길이만큼 손가락을 이동해 주세요')
            taskStore.setDirectionDrawGuideVisible(true)
            setTimeout(() => {
              App.toastInstance?.add('info', '좌우로 움직여 꺾은 선을 만들 수 있어요')
            }, 1000)
          }

          // 임시 화살표 방향 및 길이 업데이트 (방향 고정 전까지)
          if (!isDirectionFixed) {
            // 방향 고정 전: 포인터 위치 기준 스냅 체크 (성능 최적화)
            const now = performance.now()
            if (now - lastRenderTime > RENDER_THROTTLE_MS) {
              const alignedPoints = this.findAlignedBendPoints(pos)
              const hasAlignment =
                alignedPoints.horizontal.length > 0 || alignedPoints.vertical.length > 0

              // 스냅 가이드라인 표시/제거
              if (hasAlignment) {
                // 스냅된 위치로 포인터 위치 업데이트
                pos = this.getSnappedEndPoint(pos, alignedPoints)
                this.showDirectionSnapGuides(pos, alignedPoints)
              } else {
                this.clearDirectionSnapGuides()
              }
            }

            const distance = Math.sqrt((pos.x - startX) ** 2 + (pos.y - startY) ** 2)

            // 마우스 방향에 따라 임시 방향 계산 (8방향 스냅)
            const dx = pos.x - startX
            const dy = pos.y - startY
            const tempDirection = snapToDirections(dx, dy)

            // 방향성이 바뀌면 저장
            if (!lastDirection || lastDirection.index !== tempDirection.index) {
              lastDirection = tempDirection
            }

            // 거리와 방향에 따라 업데이트
            if (this.directionPreviewData.tempArrow) {
              updateTempArrow(
                this.directionPreviewData.tempArrow,
                startX,
                startY,
                tempDirection,
                distance,
              )
            }

            // 방향 고정 체크 (방향성을 결정하는 임계값)
            if (distance >= this.getDirDrawDirectionThreshold()) {
              // 방향 결정
              const dx = pos.x - startX
              const dy = pos.y - startY
              fixedDirection = snapToDirections(dx, dy)
              lastDirection = fixedDirection // 방향성 저장
              isDirectionFixed = true

              if (this.directionPreviewData.tempArrow) {
                this.directionPreviewData.tempArrow.destroy()
                this.directionPreviewData.tempArrow = undefined
              }

              // 실제 화살표 머리 생성
              this.directionPreviewData.arrow = createDirectionHeadArrow(
                this.renderer,
                this.forCreateBalloonBorderColor,
              )

              const systemLayer = this.renderer.layers.system
              if (systemLayer) {
                systemLayer.add(this.directionPreviewData.arrow)
              }
            }
          }

          // 방향이 고정되었으면 실시간 경로 계산 및 표시
          if (isDirectionFixed && fixedDirection) {
            // 초기 경로 계산
            const initialResult = calculateDirectionPath(
              startX,
              startY,
              pos.x,
              pos.y,
              fixedDirection,
              fixedLines,
              activeLineIndex,
              this.renderer.stage.scaleX(),
            )

            // 방향 고정 후: 실제 선의 끝점 기준 스냅 체크 (성능 최적화)
            let finalEndPoint = pos
            if (initialResult.path.length > 0) {
              const now = performance.now()
              if (now - lastRenderTime > RENDER_THROTTLE_MS) {
                const actualEndPoint = initialResult.path[initialResult.path.length - 1]
                const alignedPoints = this.findAlignedBendPoints(actualEndPoint)
                const hasAlignment =
                  alignedPoints.horizontal.length > 0 || alignedPoints.vertical.length > 0

                if (hasAlignment) {
                  // 스냅된 위치로 끝점 업데이트
                  finalEndPoint = this.getSnappedEndPoint(actualEndPoint, alignedPoints)
                  this.showDirectionSnapGuides(finalEndPoint, alignedPoints)
                } else {
                  this.clearDirectionSnapGuides()
                }
              }
            }

            // 스냅된 끝점으로 최종 경로 계산
            const result = calculateDirectionPath(
              startX,
              startY,
              finalEndPoint.x,
              finalEndPoint.y,
              fixedDirection,
              fixedLines,
              activeLineIndex,
              this.renderer.stage.scaleX(),
            )

            // 첫 번째 선분 길이 확인 - 임계값 이하로 줄어들면 방향성 리셋
            if (result.path.length >= 2) {
              const firstLineLength = Math.sqrt(
                Math.pow(result.path[1].x - result.path[0].x, 2) +
                  Math.pow(result.path[1].y - result.path[0].y, 2),
              )

              if (firstLineLength < this.getDirDrawDirectionThreshold()) {
                // 방향성 리셋 - 임시 화살표 상태로 복귀
                isDirectionFixed = false
                fixedDirection = null
                fixedLines = []
                activeLineIndex = 0

                // 실제 요소들 제거
                this.directionPreviewData.lines.forEach(line => line.visible(false))
                if (this.directionPreviewData.arrow) {
                  this.directionPreviewData.arrow.destroy()
                  this.directionPreviewData.arrow = undefined
                }

                // 임시 화살표 다시 생성
                const defaultDirection = lastDirection || { x: 0, y: -1, index: 1 }
                this.directionPreviewData.tempArrow = createTempArrow(
                  this.renderer,
                  defaultDirection,
                  {
                    startX,
                    startY,
                    color: this.forCreateBalloonBorderColor,
                    strokeWidth: this.connectedLineWidth,
                  },
                )

                const systemLayer = this.renderer.layers.system
                if (systemLayer) systemLayer.add(this.directionPreviewData.tempArrow)

                return // 임시 화살표 로직으로 처리되도록 함수 종료
              }
            }

            // 상태 업데이트
            fixedLines = result.newFixedLines
            activeLineIndex = result.newActiveIndex

            updateDrawingRender(result.path)
          }
        } catch (error) {
          cleanupDirectionDrawing()
          console.error('directionMouseMoveEvents error:', error)
        }
      },
    )

    // 마우스 업 - 그리기 완료
    this.eventManager.addEventFromString(stage, directionMouseUpEvents, () => {
      try {
        if (!isDrawing || drawingPath.length < 4) {
          cleanupDirectionDrawing()
          return
        }

        const distance = Math.sqrt(
          (drawingPath[drawingPath.length - 2] - startX) ** 2 +
            (drawingPath[drawingPath.length - 1] - startY) ** 2,
        )
        if (distance < this.getDirDrawDirectionThreshold()) {
          cleanupDirectionDrawing()
          return
        }

        // 실제 도형 생성을 위한 데이터 구성
        if (isDirectionFixed && fixedDirection) {
          // 현재 마우스 위치 기준으로 스냅된 최종 끝점 계산
          let currentPos: Vector2d
          try {
            currentPos = this.getPointerPosition()
          } catch (_error) {
            console.warn('Failed to get pointer position in mouseup:', _error)
            cleanupDirectionDrawing()
            return
          }

          // 초기 경로로 실제 끝점 계산
          const initialResult = calculateDirectionPath(
            startX,
            startY,
            currentPos.x,
            currentPos.y,
            fixedDirection,
            fixedLines,
            activeLineIndex,
            this.renderer.stage.scaleX(),
          )

          // 실제 선의 끝점 기준으로 스냅 체크
          let finalEndPoint = currentPos
          if (initialResult.path.length > 0) {
            const actualEndPoint = initialResult.path[initialResult.path.length - 1]
            const alignedPoints = this.findAlignedBendPoints(actualEndPoint)
            const hasAlignment =
              alignedPoints.horizontal.length > 0 || alignedPoints.vertical.length > 0

            if (hasAlignment) {
              // 스냅된 위치로 최종 끝점 설정
              finalEndPoint = this.getSnappedEndPoint(actualEndPoint, alignedPoints)
            }
          }

          // 스냅된 끝점으로 최종 경로 계산
          const result = calculateDirectionPath(
            startX,
            startY,
            finalEndPoint.x,
            finalEndPoint.y,
            fixedDirection,
            fixedLines,
            activeLineIndex,
            this.renderer.stage.scaleX(),
          )

          // 벌룬 연결선
          const points: number[] = []

          // 시작은 tempArrow와 동일한 원본 시작점
          points.push(startX, startY)

          // 꺾은점들과 마지막 끝점
          for (let i = 1; i < result.path.length; i++) {
            points.push(result.path[i].x, result.path[i].y)
          }

          // 도형 생성 (EntityFactory 사용)
          const id = this.generateShapeId()
          this.__isEntitiyCreating = false
          const directionShape = this.entityFactory.createEntity(SHAPES.DIRECTION, {
            id,
            createPoint: { x: 0, y: 0 }, // 방향도형은 절대좌표로 그리므로 createPoint는 (0,0)
            points,
            createConfig: this.getBasicCreateConfig(SHAPES.DIRECTION),
            layerId: this.selectedLayerId,
            balloonFontColor: this.forCreateBalloonFontColor,
            balloonFillColor: this.forCreateBalloonFillColor,
            balloonScale: this.forCreateBalloonScale,
            connectedLineThicknessScale: this.forCreateConnectedLineThicknessScale,
            balloonFontScale: this.forCreateBalloonFontScale,
            pointShapeScale: this.forCreatePointShapeScale,
            areaBorderThicknessScale: this.forCreateAreaBorderThicknessScale,
          })

          directionShape.node.moveToTop()
          // 상태를 정리하고 모드를 변경하기 전에 생성된 도형을 처리
          cleanupDirectionDrawing()
          this.onCreatedEntity(directionShape)

          this.__createGuideLines.forEach(line => line.destroy()) // 가이드라인 제거
          this.__createGuideLines = [] // 가이드라인 배열 초기화
        } else {
          cleanupDirectionDrawing()
        }
      } catch (error) {
        console.error('directionMouseUpEvents error:', error)
        cleanupDirectionDrawing()
      }
    })
  }

  // 브러시 그리기 이벤트
  protected addBrushDrawEvent(): void {
    const stage = this.renderer.stage
    const setState = this.setState
    let createdBrushEntity: CanvasEngineEntity | undefined = undefined

    let lastLine: ILine | undefined = undefined
    const brushEntities: CanvasEngineEntity[] = []

    // 연속 입력 감지를 위한 상태
    let continuousInputTimer: ReturnType<typeof setTimeout> | undefined
    let lastInputTime = 0
    let isWaitingForContinuousInput = false

    // 브러쉬 시작점과 최소 거리 체크용 상태
    let brushStartPoint: { x: number; y: number } | undefined = undefined
    let hasStartedDrawing = false // 실제로 브러쉬가 시작되었는지 여부

    // 연속 입력 대기 시간
    const CONTINUOUS_INPUT_WAIT_TIME = 800
    const MIN_BRUSH_DISTANCE = 5

    const createBrushWhenDistanceReached = (currentPos: { x: number; y: number }): void => {
      if (!brushStartPoint || hasStartedDrawing) return

      const distance = Math.sqrt(
        Math.pow(currentPos.x - brushStartPoint.x, 2) +
          Math.pow(currentPos.y - brushStartPoint.y, 2),
      )

      if (distance >= MIN_BRUSH_DISTANCE) {
        // 실제 브러쉬 생성 (EntityFactory 사용)
        const config = this.getDrawingConfig()

        createdBrushEntity = this.entityFactory.createEntity(SHAPES.BRUSH, {
          id: this.generateShapeId(),
          layerId: this.getLayers().find(
            (layer: CanvasEngineSoftLayer) => layer.layerName === '기본',
          )?.layerId,
          createPoint: { x: 0, y: 0 },
          createConfig: this.getBasicCreateConfig(SHAPES.BRUSH),
          brushLines: [
            {
              // 시작점을 중복시켜 6개 숫자로 생성 — 4개 숫자는 직선 스트로크 시그니처
              // (isShapeBrushPoints)로 오인되어 각진 끝(butt)으로 렌더되는 문제 방지
              points: [
                brushStartPoint.x,
                brushStartPoint.y,
                brushStartPoint.x,
                brushStartPoint.y,
                currentPos.x,
                currentPos.y,
              ],
              strokeWidth: config.thickness,
              stroke: config.color,
            },
          ],
        })

        // Brush Entity 생성 후 첫 번째 Line 찾기
        const brushGroup = createdBrushEntity.getBrush()
        if (brushGroup) {
          const lines = brushGroup.find<ILine>('Line')
          lastLine = lines[0]
        }
        brushEntities.push(createdBrushEntity)
        hasStartedDrawing = true

        setState(CANVAS_STATE.DRAWING)
      }
    }

    // 연속 입력 완료 처리
    const finalizeBrushStroke = (): void => {
      if (!createdBrushEntity || !hasStartedDrawing) {
        // 브러쉬가 생성되지 않았거나 그리기가 시작되지 않은 경우
        setState(CANVAS_STATE.IDLE)
        this.__isEntitiyCreating = false
        this.restoreEntitiyListening()

        // 상태 초기화
        createdBrushEntity = undefined
        lastLine = undefined
        isWaitingForContinuousInput = false
        brushStartPoint = undefined
        hasStartedDrawing = false
        return
      }

      const brushToSelect = createdBrushEntity

      // 상태 초기화
      createdBrushEntity = undefined
      lastLine = undefined
      isWaitingForContinuousInput = false
      brushStartPoint = undefined
      hasStartedDrawing = false

      this.__isEntitiyCreating = false
      this.onCreatedBrush(brushToSelect)
    }

    // 연속 입력 타이머
    const startContinuousInputTimer = (): void => {
      clearContinuousInputTimer()
      isWaitingForContinuousInput = true

      continuousInputTimer = setTimeout(() => {
        finalizeBrushStroke()
      }, CONTINUOUS_INPUT_WAIT_TIME)
    }

    const clearContinuousInputTimer = (): void => {
      if (continuousInputTimer) {
        clearTimeout(continuousInputTimer)
        continuousInputTimer = undefined
      }
    }

    // 브러시 이벤트 제거 및 추가
    const brushMouseDownEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_START}`
    const brushMouseMoveEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_MOVE}`
    const brushMouseUpEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_UP} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_END}`

    this.eventManager.removeEventFromString(stage, brushMouseDownEvents)
    this.eventManager.removeEventFromString(stage, brushMouseMoveEvents)
    this.eventManager.removeEventFromString(stage, brushMouseUpEvents)

    this.eventManager.addEventFromString(
      stage,
      brushMouseDownEvents,
      (_: PointerEventOf<PointerEvent>) => {
        // 스페이스바 패닝 또는 핀치줌 중이면 무시
        if (this.isSpacePressed || this.isPinchZooming) return
        if (this.__isCreateCallbackPending) return

        const currentTime = Date.now()
        const pos = this.getPointerPosition()
        clearContinuousInputTimer()

        // 연속 입력 감지 (이전 입력 후 일정 시간 내)
        const timeSinceLastInput = currentTime - lastInputTime
        const isContinuousInput =
          isWaitingForContinuousInput &&
          timeSinceLastInput < CONTINUOUS_INPUT_WAIT_TIME &&
          createdBrushEntity !== undefined &&
          hasStartedDrawing

        if (!isContinuousInput) {
          // 새로운 브러쉬 시작점 설정
          brushStartPoint = pos
          hasStartedDrawing = false
          this.__isEntitiyCreating = true
          this.disableEntityListening()
        } else {
          // 연속 입력인 경우
          if (createdBrushEntity && createdBrushEntity.avaliableShape && hasStartedDrawing) {
            const config = this.getDrawingConfig()

            const newLine = this.renderer.createLine({
              // 시작점을 중복시켜 6개 숫자로 생성 — 4개 숫자는 직선 스트로크 시그니처로
              // 오인되어 재로드 시 각진 끝(butt)으로 렌더되는 문제 방지
              points: [pos.x, pos.y, pos.x, pos.y, pos.x, pos.y],
              stroke: config.color,
              strokeWidth: config.thickness,
              lineCap: 'round',
              lineJoin: 'round',
              listening: true,
              globalCompositeOperation: 'source-over',
              hitArea: { kind: 'strokeWidth', px: (config.thickness || 0) + 50 },
            })

            const brushGroup = createdBrushEntity.avaliableShape as IGroup
            const children = brushGroup.getChildren()
            const hitArea = children.find((child: INode) => child.opacity() === 0)

            if (hitArea) {
              brushGroup.add(newLine)
              hitArea.moveToTop()
            } else {
              brushGroup.add(newLine)
            }

            lastLine = newLine
          }
          setState(CANVAS_STATE.DRAWING)
        }

        isWaitingForContinuousInput = false
        lastInputTime = currentTime
      },
    )

    this.eventManager.addEventFromString(
      stage,
      brushMouseMoveEvents,
      (e: PointerEventOf<PointerEvent>) => {
        e.evt.preventDefault()
        if (this.isPinchZooming) return
        const pos = this.renderer.getPointerPosition()
        if (!pos) return
        const adjustedPos = this.getPointerPosition()

        // 브러쉬가 아직 생성되지 않은 경우 거리 체크
        if (!hasStartedDrawing && brushStartPoint) createBrushWhenDistanceReached(adjustedPos)

        // 브러쉬가 생성되고 그리기가 시작된 경우에만 점 추가
        if (hasStartedDrawing && this.isDrawing() && lastLine && createdBrushEntity) {
          const newPoints = lastLine.points().concat([adjustedPos.x, adjustedPos.y])
          lastLine.points(newPoints)
        }
      },
    )

    this.eventManager.addEventFromString(stage, brushMouseUpEvents, () => {
      // 브러쉬가 실제로 생성되었을 때만 처리
      if (hasStartedDrawing && lastLine) {
        setState(CANVAS_STATE.IDLE)
        startContinuousInputTimer()
        lastInputTime = Date.now()
      } else {
        // 브러쉬가 생성되지 않은 경우 초기화
        brushStartPoint = undefined
        hasStartedDrawing = false
        setState(CANVAS_STATE.IDLE)
        this.__isEntitiyCreating = false
        this.restoreEntitiyListening()
      }
    })
  }

  // 브러시 직선/사각형 드래그 그리기 (데이터는 기존 브러시와 동일한 brushLines 형태로 저장)
  protected addBrushShapeDragEvent(): void {
    const stage = this.renderer.stage
    const systemLayer = this.renderer.layers.system
    const setState = this.setState

    const MIN_BRUSH_DISTANCE = 5

    let startPoint: { x: number; y: number } | undefined = undefined
    let previewLine: ILine | undefined = undefined
    let wasMultiTouch = false

    // 브러시 이벤트 제거 및 추가 (모드 전환 시 잔존 핸들러 방지)
    const brushMouseDownEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_START}`
    const brushMouseMoveEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_MOVE}`
    const brushMouseUpEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_UP} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_END}`

    this.eventManager.removeEventFromString(stage, brushMouseDownEvents)
    this.eventManager.removeEventFromString(stage, brushMouseMoveEvents)
    this.eventManager.removeEventFromString(stage, brushMouseUpEvents)

    // 시작점-현재점으로 도형 points 계산 (직선: 2점, 사각형: 닫힌 5점 폴리라인)
    const buildPoints = (
      from: { x: number; y: number },
      to: { x: number; y: number },
    ): number[] => {
      if (this.brushDrawMode === 'rect') {
        return [from.x, from.y, to.x, from.y, to.x, to.y, from.x, to.y, from.x, from.y]
      }
      return [from.x, from.y, to.x, to.y]
    }

    // 미리보기/가이드라인 정리 및 상태 복원 (확정 시엔 freehand와 동일하게 listening 복원 생략)
    const cleanup = (restoreListening: boolean = true): void => {
      previewLine?.destroy()
      previewLine = undefined
      startPoint = undefined
      this.__createGuideLines.forEach(line => line.destroy())
      this.__createGuideLines = []
      setState(CANVAS_STATE.IDLE)
      this.__isEntitiyCreating = false
      if (restoreListening) this.restoreEntitiyListening()
      systemLayer.batchDraw()
    }

    this.eventManager.addEventFromString(
      stage,
      brushMouseDownEvents,
      (e: PointerEventOf<MouseEvent | TouchEvent>) => {
        // 스페이스바 패닝 또는 핀치줌 중이면 무시
        if (this.isSpacePressed || this.isPinchZooming) return

        // 멀티터치 체크
        try {
          if (e.evt instanceof TouchEvent && e.evt.touches.length > 1) {
            wasMultiTouch = true
            return
          }
        } catch (_error) {
          // 터치 이벤트 체크 실패 시 무시
        }

        if (this.__isEntitiyCreating || this.__isCreateCallbackPending) return
        this.__isEntitiyCreating = true
        this.disableEntityListening()
        wasMultiTouch = false

        startPoint = this.getPointerPosition()

        // 미리보기 라인 생성 (systemLayer에 배치, 확정 시 브러시 엔티티로 대체)
        const config = this.getDrawingConfig()
        previewLine = this.renderer.createLine({
          points: buildPoints(startPoint, startPoint),
          stroke: config.color,
          strokeWidth: config.thickness,
          lineCap: 'butt',
          lineJoin: 'miter',
          // 사각형은 닫아서 시작/끝 모서리도 miter로 각지게
          closed: this.brushDrawMode === 'rect',
          listening: false,
        })
        systemLayer.add(previewLine)

        const stageSize = this.getStageSize()
        // 가이드라인 생성 (점선, 영역 도형 드래그와 동일한 UX)
        this.__createGuideLines.forEach(line => line.destroy())
        this.__createGuideLines = [
          this.renderer.createLine({
            points: [this.toLocalX(0), startPoint.y, stageSize.width, startPoint.y],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 상단 가이드라인
          this.renderer.createLine({
            points: [this.toLocalX(0), startPoint.y, stageSize.width, startPoint.y],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 하단 가이드라인
          this.renderer.createLine({
            points: [startPoint.x, this.toLocalY(0), startPoint.x, stageSize.height],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 좌측 가이드라인
          this.renderer.createLine({
            points: [startPoint.x, this.toLocalY(0), startPoint.x, stageSize.height],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 우측 가이드라인
        ]
        this.__createGuideLines.forEach(line => systemLayer.add(line))

        setState(CANVAS_STATE.DRAWING)
      },
    )

    this.eventManager.addEventFromString(
      stage,
      brushMouseMoveEvents,
      (e: PointerEventOf<PointerEvent>) => {
        e.evt.preventDefault()
        if (this.isPinchZooming) return
        if (!startPoint || !previewLine) return

        const pos = this.getPointerPosition()
        previewLine.points(buildPoints(startPoint, pos))

        const stageSize = this.getStageSize()
        // 가이드라인 업데이트
        this.__createGuideLines[0]?.points([
          this.toLocalX(0),
          startPoint.y,
          stageSize.width,
          startPoint.y,
        ]) // 상단
        this.__createGuideLines[1]?.points([this.toLocalX(0), pos.y, stageSize.width, pos.y]) // 하단
        this.__createGuideLines[2]?.points([
          startPoint.x,
          this.toLocalY(0),
          startPoint.x,
          stageSize.height,
        ]) // 좌측
        this.__createGuideLines[3]?.points([pos.x, this.toLocalY(0), pos.x, stageSize.height]) // 우측

        systemLayer.batchDraw()
      },
    )

    this.eventManager.addEventFromString(stage, brushMouseUpEvents, () => {
      // 멀티터치였으면 도형 생성하지 않고 정리
      if (wasMultiTouch) {
        wasMultiTouch = false
        cleanup()
        return
      }

      if (!startPoint || !previewLine) return

      const pos = this.getPointerPosition()
      const distance = Math.sqrt(
        Math.pow(pos.x - startPoint.x, 2) + Math.pow(pos.y - startPoint.y, 2),
      )

      // 최소 거리 미달이면 생성하지 않음
      if (distance < MIN_BRUSH_DISTANCE) {
        cleanup()
        return
      }

      const points = buildPoints(startPoint, pos)
      const config = this.getDrawingConfig()

      cleanup(false)

      // 드래그 1회 = 스트로크 1개 즉시 확정 (기존 브러시와 동일한 데이터 형태)
      const createdBrushEntity = this.entityFactory.createEntity(SHAPES.BRUSH, {
        id: this.generateShapeId(),
        layerId: this.getLayers().find((layer: CanvasEngineSoftLayer) => layer.layerName === '기본')
          ?.layerId,
        createPoint: { x: 0, y: 0 },
        createConfig: this.getBasicCreateConfig(SHAPES.BRUSH),
        brushLines: [
          {
            points,
            strokeWidth: config.thickness,
            stroke: config.color,
          },
        ],
      })

      this.onCreatedBrush(createdBrushEntity)
    })
  }

  // 지우개 커서 정리
  protected cleanupEraserCursor(): void {
    const systemLayer = this.renderer.layers.system
    const stage = this.renderer.stage
    const cursors = systemLayer.find('.eraser-cursor')
    cursors.forEach((cursor: INode) => cursor.destroy())
    stage.off('mouseleave.eraser_cursor')
    stage.off('mouseenter.eraser_cursor')
    systemLayer.batchDraw()
  }

  // 지우개 ↔ 그리기 전환 시 이벤트 재설정
  refreshDrawModeEvents(): void {
    if (this.canvasHandleMode !== CANVAS_HANDLE_MODE.DRAW) return
    this.changeCanvasHandleMode(CANVAS_HANDLE_MODE.DRAW)
  }

  // 브러시 지우개 이벤트
  protected addBrushEraserEvent(): void {
    const stage = this.renderer.stage
    const systemLayer = this.renderer.layers.system
    let isErasing = false
    let eraserCursor: ICircle | null = null

    // undo/redo용 스냅샷 저장
    interface LineSnapshot {
      line: ILine
      originalPoints: number[]
      parentGroup: IGroup
      stroke: string
      strokeWidth: number
    }
    interface EntitySnapshot {
      entity: CanvasEngineEntity
      wasRemoved: boolean
    }
    let lineSnapshots: LineSnapshot[] = []
    let entitySnapshots: EntitySnapshot[] = []
    // 추가된 라인 추적 (undo 시 제거, parentGroup으로 redo 시 원래 그룹에 복원)
    let addedLines: Array<{ line: ILine; parentGroup: IGroup }> = []
    // 서버 저장용 변경/삭제 엔티티 ID 추적
    let modifiedEntityIds = new Set<string>()
    // 삭제된 엔티티: id → 캔버스 인덱스 (undo 복원 위치용)
    let deletedEntityMap = new Map<string, number>()

    const createEraserCursor = (): void => {
      if (eraserCursor) eraserCursor.destroy()
      const scale = stage.scaleX()
      const radius = getEraserRadius(this.eraserThickness)
      eraserCursor = this.renderer.createCircle({
        name: 'eraser-cursor',
        radius,
        stroke: '#333',
        strokeWidth: 2 / scale,
        fill: 'rgba(0, 0, 0, 0.06)',
        listening: false,
        visible: true,
      })
      systemLayer.add(eraserCursor)
    }

    const updateEraserCursor = (pos: { x: number; y: number }): void => {
      if (!eraserCursor) createEraserCursor()
      const scale = stage.scaleX()
      const radius = getEraserRadius(this.eraserThickness)
      eraserCursor!.radius(radius)
      eraserCursor!.strokeWidth(2 / scale)
      eraserCursor!.position(pos)
      systemLayer.batchDraw()
    }

    const performErase = (pos: { x: number; y: number }): void => {
      const eraserRadius = getEraserRadius(this.eraserThickness)
      const entities = this.getEntities()

      for (const entity of entities) {
        if (!entity.isBrush()) continue

        if (!entity.avaliableShape) continue
        const brushGroup = entity.avaliableShape as IGroup

        const allLines = brushGroup.find<ILine>('Line')
        const visibleLines = allLines.filter(
          (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
        )

        const linesToProcess: Array<{ line: ILine; segments: number[][] }> = []

        for (const line of visibleLines) {
          const points = line.points()
          const segments = eraseFromLine(points, pos.x, pos.y, eraserRadius)

          // 변경이 있는 경우만 처리
          if (segments.length === 1 && segments[0].length === points.length) continue
          if (segments.length === 0 && points.length < 4) continue

          // 원본 스냅샷 저장 (첫 번째 변경 시만)
          if (!lineSnapshots.find(s => s.line === line)) {
            lineSnapshots.push({
              line,
              originalPoints: [...points],
              parentGroup: brushGroup,
              stroke: line.stroke() as string,
              strokeWidth: line.strokeWidth(),
            })
          }

          // 직선/사각형 스트로크는 부분 삭제 없이 오브젝트 단위로 삭제
          linesToProcess.push({ line, segments: isShapeBrushPoints(points) ? [] : segments })
        }

        for (const { line, segments } of linesToProcess) {
          if (segments.length === 0) {
            line.destroy()
          } else if (segments.length === 1) {
            line.points(segments[0])
          } else {
            // 첫 번째 세그먼트로 기존 라인 업데이트
            line.points(segments[0])
            // 나머지 세그먼트는 새 라인으로 추가
            for (let i = 1; i < segments.length; i++) {
              const newLine = this.renderer.createLine({
                points: segments[i],
                stroke: line.stroke(),
                strokeWidth: line.strokeWidth(),
                lineCap: 'round',
                lineJoin: 'round',
                tension: 0.5,
                listening: true,
              })
              brushGroup.add(newLine)
              addedLines.push({ line: newLine, parentGroup: brushGroup })
            }
          }
        }

        // 변경된 엔티티의 캐시 갱신 (서버에서 불러온 엔티티는 캐시됨)
        if (linesToProcess.length > 0) {
          if (this.renderer.hints.isStatic(entity.node))
            this.renderer.hints.unmarkStatic(entity.node)
          modifiedEntityIds.add(entity.getId())
        }

        // 모든 라인이 삭제된 경우 엔티티 제거
        const remainingLines = brushGroup.find<ILine>('Line')
        const remainingVisible = remainingLines.filter(
          (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
        )
        if (remainingVisible.length === 0 && linesToProcess.length > 0) {
          if (!entitySnapshots.find(s => s.entity === entity)) {
            entitySnapshots.push({ entity, wasRemoved: true })
          }
          const entityId = entity.getId()
          // 삭제 전 인덱스 캡처 (undo 복원 위치용)
          const entityIndex = this.getEntities().indexOf(entity)
          modifiedEntityIds.delete(entityId)
          deletedEntityMap.set(entityId, entityIndex)
          this.removeEntity(entityId)
        }
      }

      this.renderer.requestDraw()
    }

    // 이벤트 문자열
    const eraserMouseDownEvents = `${CANVAS_HANDLE_EVENT.ERASER_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.ERASER_TOUCH_START}`
    const eraserMouseMoveEvents = `${CANVAS_HANDLE_EVENT.ERASER_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.ERASER_TOUCH_MOVE}`
    const eraserMouseUpEvents = `${CANVAS_HANDLE_EVENT.ERASER_MOUSE_UP} ${CANVAS_HANDLE_EVENT.ERASER_TOUCH_END}`

    // 기존 이벤트 제거
    this.eventManager.removeEventFromString(stage, eraserMouseDownEvents)
    this.eventManager.removeEventFromString(stage, eraserMouseMoveEvents)
    this.eventManager.removeEventFromString(stage, eraserMouseUpEvents)

    // 브러시 그리기 이벤트도 제거 (모드 전환 시 충돌 방지)
    const brushMouseDownEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_START}`
    const brushMouseMoveEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_MOVE}`
    const brushMouseUpEvents = `${CANVAS_HANDLE_EVENT.BRUSH_MOUSE_UP} ${CANVAS_HANDLE_EVENT.BRUSH_TOUCH_END}`
    this.eventManager.removeEventFromString(stage, brushMouseDownEvents)
    this.eventManager.removeEventFromString(stage, brushMouseMoveEvents)
    this.eventManager.removeEventFromString(stage, brushMouseUpEvents)

    this.eventManager.addEventFromString(
      stage,
      eraserMouseDownEvents,
      (_: PointerEventOf<PointerEvent>) => {
        if (this.isSpacePressed || this.isPinchZooming) return
        isErasing = true
        lineSnapshots = []
        entitySnapshots = []
        addedLines = []
        modifiedEntityIds = new Set()
        deletedEntityMap = new Map()

        const pos = this.getPointerPosition()
        updateEraserCursor(pos)
        performErase(pos)
      },
    )

    this.eventManager.addEventFromString(
      stage,
      eraserMouseMoveEvents,
      (e: PointerEventOf<PointerEvent>) => {
        e.evt.preventDefault()
        if (this.isPinchZooming) return
        const pos = this.getPointerPosition()
        updateEraserCursor(pos)

        if (isErasing) {
          performErase(pos)
        }
      },
    )

    this.eventManager.addEventFromString(stage, eraserMouseUpEvents, async () => {
      if (!isErasing) return
      isErasing = false

      // batch 시작: 캔버스 undo + 데이터 undo를 하나의 엔트리로 통합
      undoRedoManager.startBatch()

      // undo/redo 등록 (변경이 있는 경우만)
      if (lineSnapshots.length > 0 || entitySnapshots.length > 0) {
        const capturedLineSnapshots = [...lineSnapshots]
        const capturedAddedLines = [...addedLines]

        // redo용으로 현재(지워진) 상태 스냅샷 저장
        const erasedStates = capturedLineSnapshots.map(s => ({
          currentPoints: s.line.getParent() ? [...s.line.points()] : ([] as number[]),
          wasDestroyed: !s.line.getParent(),
        }))

        undoRedoManager.execute(
          '브러시 지우기',
          // undo: 원래 상태로 복원
          () => {
            for (const { line } of capturedAddedLines) {
              line.destroy()
            }
            for (const snapshot of capturedLineSnapshots) {
              if (!snapshot.line.getParent()) {
                // 직선/사각형 스트로크는 곡선 보간 없이 각진 끝/모서리로 복원
                const isShapeStroke = isShapeBrushPoints(snapshot.originalPoints)
                const newLine = this.renderer.createLine({
                  points: snapshot.originalPoints,
                  stroke: snapshot.stroke,
                  strokeWidth: snapshot.strokeWidth,
                  lineCap: isShapeStroke ? 'butt' : 'round',
                  lineJoin: isShapeStroke ? 'miter' : 'round',
                  tension: isShapeStroke ? 0 : 0.5,
                  // 사각형은 닫아서 시작/끝 모서리도 miter로 각지게
                  closed: isShapeStroke && snapshot.originalPoints.length === 10,
                  listening: true,
                })
                snapshot.parentGroup.add(newLine)
                snapshot.line = newLine
              } else {
                snapshot.line.points(snapshot.originalPoints)
              }
            }
            this.renderer.requestDraw()
          },
          // redo: 지워진 상태로 다시 적용
          () => {
            for (let i = 0; i < capturedLineSnapshots.length; i++) {
              const snapshot = capturedLineSnapshots[i]
              const erasedState = erasedStates[i]
              if (erasedState.wasDestroyed) {
                snapshot.line.destroy()
              } else {
                snapshot.line.points(erasedState.currentPoints)
              }
            }
            for (const { line, parentGroup } of capturedAddedLines) {
              if (!line.getParent()) {
                parentGroup.add(line)
              }
            }
            this.renderer.requestDraw()
          },
        )
      }

      // 서버/SQLite 저장 (컨펌/toast 없이 조용히 처리)
      if (modifiedEntityIds.size > 0 || deletedEntityMap.size > 0) {
        const deletedEntries = Object.fromEntries(deletedEntityMap)
        await this.events.onBrushErased([...modifiedEntityIds], deletedEntries)
      }
      undoRedoManager.endBatch('브러시 지우기')

      lineSnapshots = []
      entitySnapshots = []
      addedLines = []
      modifiedEntityIds = new Set()
      deletedEntityMap = new Map()
    })

    // 초기 커서 생성
    createEraserCursor()

    // 모드 전환 시 커서 정리를 위한 cleanup 등록은 changeCanvasHandleMode에서 처리
    // 여기서는 stage의 mouseleave에서 커서 숨기기
    stage.on('mouseleave.eraser_cursor', () => {
      if (eraserCursor) eraserCursor.visible(false)
      systemLayer.batchDraw()
    })
    stage.on('mouseenter.eraser_cursor', () => {
      if (eraserCursor) eraserCursor.visible(true)
      systemLayer.batchDraw()
    })
  }

  protected __createGuideLines: ILine[] = []

  protected createShapeDragEvent(): void {
    const stage = this.renderer.stage
    const systemLayer = this.renderer.layers.system

    this.__createGuideLines.forEach(line => line.destroy())
    this.__createGuideLines = []
    let startX = 0
    let startY = 0
    let wasMultiTouch = false // 멀티터치 여부 추적

    let createdShape: CanvasEngineEntity | undefined = undefined

    // 이벤트 문자열
    const dragMouseDownEvents = `${CANVAS_HANDLE_EVENT.CREATE_DRAG_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.CREATE_DRAG_TOUCH_START}`
    const dragMouseMoveEvents = `${CANVAS_HANDLE_EVENT.CREATE_DRAG_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.CREATE_DRAG_TOUCH_MOVE}`
    const dragMouseUpEvents = `${CANVAS_HANDLE_EVENT.CREATE_DRAG_MOUSE_UP} ${CANVAS_HANDLE_EVENT.CREATE_DRAG_TOUCH_END}`

    // mousedown 이벤트 처리
    this.eventManager.addEventFromString(
      stage,
      dragMouseDownEvents,
      async (e: PointerEventOf<MouseEvent | TouchEvent>) => {
        // 스페이스바 패닝 또는 핀치줌 중이면 무시
        if (this.isSpacePressed || this.isPinchZooming) return

        // 멀티터치 체크
        try {
          if (e.evt instanceof TouchEvent && e.evt.touches.length > 1) {
            wasMultiTouch = true
            return
          }
        } catch (_error) {
          // 터치 이벤트 체크 실패 시 무시
        }

        if (this.__isEntitiyCreating || this.__isCreateCallbackPending) return
        this.__isEntitiyCreating = true
        wasMultiTouch = false
        this.__createGuideLines.forEach(line => line.destroy()) // 가이드라인 제거

        const pos = this.getPointerPosition()
        // 시작점 설정
        //startX = engine.toLocalX(getVector2dFromEvent(e).x);
        //startY = engine.toLocalY(getVector2dFromEvent(e).y);
        startX = pos.x
        startY = pos.y

        const SHAPE = this.getCreateEntityType()
        const shapeId = this.generateShapeId()

        // Entity 생성 (EntityFactory 사용)
        createdShape = this.entityFactory.createEntity(SHAPE, {
          id: shapeId,
          createPoint: { x: startX, y: startY },
          createConfig: this.getBasicCreateConfig(SHAPE),
          layerId: this.selectedLayerId,
          balloonFontColor: this.forCreateBalloonFontColor,
          balloonFillColor: this.forCreateBalloonFillColor,
          balloonScale: this.forCreateBalloonScale,
          connectedLineThicknessScale: this.forCreateConnectedLineThicknessScale,
          balloonFontScale: this.forCreateBalloonFontScale,
          pointShapeScale: this.forCreatePointShapeScale,
          areaBorderThicknessScale:
            SHAPE === SHAPES.RECT ? this.forCreateAreaBorderThicknessScale : 1,
        })

        const stageSize = this.getStageSize()
        // 가이드라인 생성 (점선)
        this.__createGuideLines = [
          this.renderer.createLine({
            points: [this.toLocalX(0), startY, stageSize.width, startY],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 상단 가이드라인
          this.renderer.createLine({
            points: [this.toLocalX(0), startY, stageSize.width, startY],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 하단 가이드라인
          this.renderer.createLine({
            points: [startX, this.toLocalY(0), startX, stageSize.height],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 좌측 가이드라인
          this.renderer.createLine({
            points: [startX, this.toLocalY(0), startX, stageSize.height],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 우측 가이드라인
        ]
        this.__createGuideLines.forEach(line => systemLayer!.add(line))
      },
    )

    // mousemove 이벤트 처리

    this.eventManager.addEventFromString(stage, dragMouseMoveEvents, () => {
      if (!createdShape) return

      // 현재 위치 계산

      const pos = this.getPointerPosition()

      const currentX = pos.x
      const currentY = pos.y

      // 도형 크기 업데이트
      switch (true) {
        case createdShape.isRect(): {
          const rect = createdShape.avaliableShape as IRect
          rect.width(Math.abs(currentX - startX))
          rect.height(Math.abs(currentY - startY))
          // 도형 위치 업데이트
          rect.x(Math.min(currentX - startX, 0))
          rect.y(Math.min(currentY - startY, 0))
          break
        }
        case createdShape.isEllipse(): {
          const ellipse = createdShape.avaliableShape as INode
          ellipse.width(Math.abs(currentX - startX))
          ellipse.height(Math.abs(currentY - startY))

          const _shape = createdShape.node.getChildren()[0]

          createdShape.node.x(Math.min(currentX, startX) + _shape.width() / 2)
          createdShape.node.y(Math.min(currentY, startY) + _shape.height() / 2)
          _shape.fire('transform')
          break
        }
        case createdShape.isLine(): {
          const points = createdShape.getPoints()
          // 기존의 시작점과 새로운 끝점을 사용하여 선을 업데이트
          createdShape.setPoints([points[0], points[1], currentX, currentY])
          break
        }
      }

      const stageSize = this.getStageSize()
      // 가이드라인 업데이트
      this.__createGuideLines[0].points([this.toLocalX(0), startY, stageSize.width, startY]) // 상단
      this.__createGuideLines[1].points([this.toLocalX(0), currentY, stageSize.width, currentY]) // 하단
      this.__createGuideLines[2].points([startX, this.toLocalY(0), startX, stageSize.height]) // 좌측
      this.__createGuideLines[3].points([currentX, this.toLocalY(0), currentX, stageSize.height]) // 우측
      createdShape.node.moveToTop()
      this.renderer.layers.system.batchDraw()
    })

    // mouseup 이벤트 처리
    this.eventManager.addEventFromString(stage, dragMouseUpEvents, () => {
      this.__isEntitiyCreating = false

      // 멀티터치였으면 도형 생성하지 않고 정리
      if (wasMultiTouch) {
        if (createdShape) {
          createdShape.node.destroy()
          createdShape = undefined
        }
        this.__createGuideLines.forEach(line => line.destroy())
        this.__createGuideLines = []
        wasMultiTouch = false
        return
      }

      if (!createdShape) return

      this.onCreatedEntity(createdShape)
      this.__createGuideLines.forEach(line => line.destroy()) // 가이드라인 제거
      this.__createGuideLines = [] // 가이드라인 배열 초기화
      createdShape = undefined // 드래그 종료 후 rect 초기화
    })
  }

  protected createShapeDragAndDropEvent(): void {
    const stage = this.renderer.stage

    let DrawingLine: ILine | undefined = undefined
    let targetX = 0
    let targetY = 0
    let isDragging = false
    let wasMultiTouch = false // 멀티터치 여부 추적
    this.__createGuideLines = []

    // 이벤트 문자열
    const dragAndDropMouseDownEvents = `${CANVAS_HANDLE_EVENT.CREATE_DRAG_AND_DROP_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.CREATE_DRAG_AND_DROP_TOUCH_START}`
    const dragAndDropMouseMoveEvents = `${CANVAS_HANDLE_EVENT.CREATE_DRAG_AND_DROP_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.CREATE_DRAG_AND_DROP_TOUCH_MOVE}`
    const dragAndDropMouseUpEvents = `${CANVAS_HANDLE_EVENT.CREATE_DRAG_AND_DROP_MOUSE_UP} ${CANVAS_HANDLE_EVENT.CREATE_DRAG_AND_DROP_TOUCH_END}`

    // mousedown 이벤트 처리
    this.eventManager.addEventFromString(
      stage,
      dragAndDropMouseDownEvents,
      (e: PointerEventOf<MouseEvent | TouchEvent>) => {
        // 스페이스바 패닝 또는 핀치줌 중이면 무시
        if (this.isSpacePressed || this.isPinchZooming) return

        // 멀티터치 체크
        try {
          if (e.evt instanceof TouchEvent && e.evt.touches.length > 1) {
            wasMultiTouch = true
            return
          }
        } catch (_error) {
          // 터치 이벤트 체크 실패 시 무시
        }

        if (this.__isEntitiyCreating || this.__isCreateCallbackPending) return
        this.__isEntitiyCreating = true
        wasMultiTouch = false
        this.__createGuideLines.forEach(line => line.destroy()) // 가이드라인 제거

        isDragging = true
        // 시작점 설정

        const pos = this.getPointerPosition()
        //targetX = engine.toLocalX(e.evt.layerX);
        //targetY = engine.toLocalY(e.evt.layerY);

        targetX = pos.x
        targetY = pos.y

        if (DrawingLine) {
          this.__createGuideLines.forEach(line => line.destroy()) // 가이드라인 제거
          this.__createGuideLines = [] // 가이드라인 배열 초기화

          const points = DrawingLine.points()
          DrawingLine.points([points[0], points[1], targetX, targetY])
          isDragging = false
          DrawingLine = undefined
          return
        }

        // 가이드라인 생성 (점선)

        const stageSize = this.getStageSize()
        this.__createGuideLines = [
          this.renderer.createLine({
            points: [this.toLocalX(0), targetY, stageSize.width, targetY],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 하단 가이드라인
          this.renderer.createLine({
            points: [targetX, this.toLocalY(0), targetX, stageSize.height],
            stroke: this.guideLineColor,
            dash: [4, 4],
          }), // 우측 가이드라인
        ]
        this.__createGuideLines.forEach(line => this.renderer.layers.system!.add(line))
      },
    )

    // mousemove 이벤트 처리
    this.eventManager.addEventFromString(stage, dragAndDropMouseMoveEvents, () => {
      if (!isDragging) return
      // 현재 위치 계산

      const pos = this.getPointerPosition()
      //targetX = engine.toLocalX(e.evt.layerX);
      //targetY = engine.toLocalY(e.evt.layerY);

      targetX = pos.x
      targetY = pos.y

      // 가이드라인 업데이트
      const stageSize = this.getStageSize()
      this.__createGuideLines[0].points([this.toLocalX(0), targetY, stageSize.width, targetY]) // 하단
      this.__createGuideLines[1].points([targetX, this.toLocalY(0), targetX, stageSize.height]) // 우측

      if (DrawingLine) {
        const points = DrawingLine.points()
        DrawingLine.points([points[0], points[1], targetX, targetY])
        DrawingLine.fire('transform')
      }
      this.renderer.layers.system.batchDraw()
    })

    // mouseup 이벤트 처리
    this.eventManager.addEventFromString(stage, dragAndDropMouseUpEvents, async () => {
      this.__isEntitiyCreating = false

      // 멀티터치였으면 도형 생성하지 않고 정리
      if (wasMultiTouch) {
        this.__createGuideLines.forEach(line => line.destroy())
        this.__createGuideLines = []
        isDragging = false
        wasMultiTouch = false
        return
      }

      const SHAPE = this.getCreateEntityType()
      const shapeId = this.generateShapeId()
      // Entity 생성 (EntityFactory 사용)
      const shape = this.entityFactory.createEntity(SHAPE, {
        id: shapeId,
        createPoint: { x: targetX, y: targetY },
        createConfig: this.getBasicCreateConfig(SHAPE),
        layerId: this.selectedLayerId,
        balloonFontColor: this.forCreateBalloonFontColor,
        balloonFillColor: this.forCreateBalloonFillColor,
        balloonScale: this.forCreateBalloonScale,
        connectedLineThicknessScale: this.forCreateConnectedLineThicknessScale,
        balloonFontScale: this.forCreateBalloonFontScale,
        pointShapeScale: this.forCreatePointShapeScale,
        areaBorderThicknessScale:
          SHAPE === SHAPES.RECT ? this.forCreateAreaBorderThicknessScale : 1,
      })

      if (shape.isLine()) {
        DrawingLine = shape.getLine()
      }
      shape.node.moveToTop()

      if (!shape.isLine()) {
        this.onCreatedEntity(shape)

        this.__createGuideLines.forEach(line => line.destroy()) // 가이드라인 제거
        this.__createGuideLines = [] // 가이드라인 배열 초기화

        isDragging = false
      }
    })
  }

  protected createShapeMouseUpEvent(): void {
    const stage = this.renderer.stage
    const systemLayer = this.renderer.layers.system

    this.__createGuideLines = []
    let startX = 0
    let startY = 0
    let currentX = 0
    let currentY = 0
    let isTouching = false
    let isDragging = false
    let rectGuide: IRect | undefined = undefined
    let rectGuideBorder: IRect | undefined = undefined
    let wasMultiTouch = false // 멀티터치 여부 추적

    // 이벤트 문자열
    const mouseUpMouseDownEvents = `${CANVAS_HANDLE_EVENT.CREATE_MOUSE_UP_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.CREATE_MOUSE_UP_TOUCH_START}`
    const mouseUpMouseMoveEvents = `${CANVAS_HANDLE_EVENT.CREATE_MOUSE_UP_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.CREATE_MOUSE_UP_TOUCH_MOVE}`
    const mouseUpMouseUpEvents = `${CANVAS_HANDLE_EVENT.CREATE_MOUSE_UP_MOUSE_UP} ${CANVAS_HANDLE_EVENT.CREATE_MOUSE_UP_TOUCH_END}`

    // 가이드 정리 함수
    const cleanupGuides = (): void => {
      this.__createGuideLines.forEach(line => line.destroy())
      this.__createGuideLines = []
      if (rectGuide) {
        rectGuide.destroy()
        rectGuide = undefined
      }
      if (rectGuideBorder) {
        rectGuideBorder.destroy()
        rectGuideBorder = undefined
      }
    }

    // RECT 가이드 생성/업데이트 함수
    const updateRectGuide = (x1: number, y1: number, x2: number, y2: number): void => {
      const minX = Math.min(x1, x2)
      const minY = Math.min(y1, y2)
      const width = Math.abs(x2 - x1)
      const height = Math.abs(y2 - y1)

      // 채우기 가이드
      if (!rectGuide && systemLayer) {
        rectGuide = this.renderer.createRect({
          fill: RECT_DRAG_GUIDE_COLOR,
          opacity: RECT_DRAG_GUIDE_OPACITY,
          listening: false,
        })
        systemLayer.add(rectGuide)
      }

      // 테두리 가이드
      if (!rectGuideBorder && systemLayer) {
        rectGuideBorder = this.renderer.createRect({
          stroke: RECT_DRAG_GUIDE_COLOR,
          strokeWidth: 2,
          dash: RECT_DRAG_GUIDE_DASH,
          fill: undefined,
          listening: false,
        })
        systemLayer.add(rectGuideBorder)
      }

      // 가이드 위치 및 크기 업데이트
      if (rectGuide) {
        rectGuide.x(minX)
        rectGuide.y(minY)
        rectGuide.width(width)
        rectGuide.height(height)
      }

      if (rectGuideBorder) {
        rectGuideBorder.x(minX)
        rectGuideBorder.y(minY)
        rectGuideBorder.width(width)
        rectGuideBorder.height(height)
      }
    }

    // 터치 시작 이벤트 처리
    this.eventManager.addEventFromString(
      stage,
      mouseUpMouseDownEvents,
      (e: PointerEventOf<MouseEvent | TouchEvent>) => {
        // 스페이스바 패닝 또는 핀치줌 중이면 무시
        if (this.isSpacePressed || this.isPinchZooming) return

        // 멀티터치 체크
        try {
          if (e.evt instanceof TouchEvent && e.evt.touches.length > 1) {
            wasMultiTouch = true
            cleanupGuides()
            isTouching = false
            this.__isEntitiyCreating = false
            return
          }
        } catch (_error) {
          // 터치 이벤트 체크 실패 시 무시
        }

        // 터치 이벤트인 경우 마우스 이벤트 중복 방지
        if (e.evt.type.startsWith('touch')) {
          e.evt.preventDefault()
        }

        if (this.__isEntitiyCreating || this.__isCreateCallbackPending) return
        this.__isEntitiyCreating = true
        wasMultiTouch = false
        cleanupGuides()

        const pos = this.getPointerPosition()
        startX = pos.x
        startY = pos.y
        currentX = pos.x
        currentY = pos.y
        isTouching = true
        isDragging = false

        // RECT가 아닌 경우 기존 십자 가이드라인 생성
        const SHAPE = this.getCreateEntityType()
        if (SHAPE !== SHAPES.RECT) {
          this.__createGuideLines = [
            this.renderer.createLine({
              points: [this.toLocalX(0), startY, this.toLocalX(stage.width()), startY],
              stroke: this.guideLineColor,
              dash: [4, 4],
            }), // 가로 가이드라인
            this.renderer.createLine({
              points: [startX, this.toLocalY(0), startX, this.toLocalY(stage.height())],
              stroke: this.guideLineColor,
              dash: [4, 4],
            }), // 세로 가이드라인
          ]
          this.__createGuideLines.forEach(line => systemLayer!.add(line))
        }
      },
    )

    // 터치 이동 이벤트 처리
    this.eventManager.addEventFromString(
      stage,
      mouseUpMouseMoveEvents,
      (e: PointerEventOf<MouseEvent | TouchEvent>) => {
        if (!isTouching) return

        // 멀티터치가 감지되면 생성 취소
        try {
          if (e.evt instanceof TouchEvent && e.evt.touches.length > 1) {
            wasMultiTouch = true
            cleanupGuides()
            isTouching = false
            this.__isEntitiyCreating = false
            return
          }
        } catch (_error) {
          // 터치 이벤트 체크 실패 시 무시
        }

        const pos = this.getPointerPosition()
        currentX = pos.x
        currentY = pos.y

        const SHAPE = this.getCreateEntityType()

        if (SHAPE === SHAPES.RECT) {
          // RECT의 경우 - 드래그 가이드 적용
          const dragDistance = Math.sqrt(
            Math.pow(currentX - startX, 2) + Math.pow(currentY - startY, 2),
          )

          // 최소 거리 이상이면 드래그 모드 활성화
          if (dragDistance >= RECT_DRAG_MIN_DISTANCE) {
            if (!isDragging) {
              isDragging = true
              // 기존 가이드라인 제거 (있을 경우)
              cleanupGuides()
            }

            // RECT 가이드 업데이트
            updateRectGuide(startX, startY, currentX, currentY)
            systemLayer?.batchDraw()
          }
        } else {
          // 다른 shape의 경우 - 기존 십자 가이드라인 업데이트
          if (this.__createGuideLines.length >= 2) {
            this.__createGuideLines[0].points([
              this.toLocalX(0),
              currentY,
              this.toLocalX(stage.width()),
              currentY,
            ]) // 가로 가이드라인
            this.__createGuideLines[1].points([
              currentX,
              this.toLocalY(0),
              currentX,
              this.toLocalY(stage.height()),
            ]) // 세로 가이드라인

            systemLayer?.batchDraw()
          }
        }
      },
    )

    // 터치 종료 이벤트 처리
    this.eventManager.addEventFromString(
      stage,
      mouseUpMouseUpEvents,
      async (e: PointerEventOf<MouseEvent | TouchEvent>) => {
        // 터치 이벤트인 경우 마우스 이벤트 중복 방지
        if (e.evt.type.startsWith('touch')) {
          e.evt.preventDefault()
        }

        this.__isEntitiyCreating = false

        // 멀티터치였거나 터치 중이 아니면 도형 생성하지 않음
        if (wasMultiTouch || !isTouching) {
          cleanupGuides()
          isTouching = false
          isDragging = false
          wasMultiTouch = false
          return
        }

        const SHAPE = this.getCreateEntityType()
        const shapeId = this.generateShapeId()

        let shape: CanvasEngineEntity

        if (isDragging && SHAPE === SHAPES.RECT) {
          // RECT 드래그한 경우: 드래그된 영역만큼의 RECT 생성 (EntityFactory 사용)
          const minX = Math.min(startX, currentX)
          const minY = Math.min(startY, currentY)
          const width = Math.abs(currentX - startX)
          const height = Math.abs(currentY - startY)

          // 최소 크기 보장
          const finalWidth = Math.max(width, 10)
          const finalHeight = Math.max(height, 10)

          shape = this.entityFactory.createEntity(SHAPE, {
            id: shapeId,
            createPoint: { x: minX, y: minY },
            createConfig: {
              width: finalWidth,
              height: finalHeight,
              color: this.forCreateBalloonBorderColor,
            },
            layerId: this.selectedLayerId,
            balloonFontColor: this.forCreateBalloonFontColor,
            balloonFillColor: this.forCreateBalloonFillColor,
            balloonScale: this.forCreateBalloonScale,
            connectedLineThicknessScale: this.forCreateConnectedLineThicknessScale,
            balloonFontScale: this.forCreateBalloonFontScale,
            pointShapeScale: this.forCreatePointShapeScale,
            areaBorderThicknessScale:
              SHAPE === SHAPES.RECT ? this.forCreateAreaBorderThicknessScale : 1,
          })
        } else {
          // RECT가 아니거나 RECT 단순 터치한 경우: 기본 크기로 생성 (EntityFactory 사용)
          shape = this.entityFactory.createEntity(SHAPE, {
            id: shapeId,
            createPoint: { x: currentX, y: currentY },
            createConfig: this.getBasicCreateConfig(SHAPE),
            layerId: this.selectedLayerId,
            balloonFontColor: this.forCreateBalloonFontColor,
            balloonFillColor: this.forCreateBalloonFillColor,
            balloonScale: this.forCreateBalloonScale,
            connectedLineThicknessScale: this.forCreateConnectedLineThicknessScale,
            balloonFontScale: this.forCreateBalloonFontScale,
            pointShapeScale: this.forCreatePointShapeScale,
            areaBorderThicknessScale:
              SHAPE === SHAPES.RECT ? this.forCreateAreaBorderThicknessScale : 1,
          })

          // LINE의 경우는 기존 로직 유지
          if (shape.isLine()) {
            shape.setPoints([currentX - 20, currentY, currentX + 20, currentY])
          }
        }

        shape.node.moveToTop()
        this.onCreatedEntity(shape)
        // 가이드 정리
        cleanupGuides()

        // 상태 초기화
        isTouching = false
        isDragging = false

        this.restoreAllEntitiyListening()
      },
    )
  }

  /**
   * 스테이지 이벤트 초기화
   */
  protected initStageEvent = (): void => {
    const stage = this.renderer.stage

    for (const event of Object.values(CANVAS_HANDLE_EVENT)) {
      if (event.endsWith('_system')) continue
      this.eventManager.removeEvent(stage, event)
    }
  }
}
